// src/components/CapacityOutlook.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../utils/firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Menu, X, Search, Calendar, Filter, FileText } from 'lucide-react';
import logo from '../assets/images/logo2.PNG';
import { AdminOnly, useUserRole } from '../contexts/UserContext';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';
import PremiumFeatureModal from './PremiumFeatureModal';
import MonthlyReportGenerator from './MonthlyReportGenerator';

const CapacityOutlook = () => {
  const navigate = useNavigate();
  
  // State
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [clubId, setClubId] = useState(null);
  const [clubName, setClubName] = useState('');
  const [branding, setBranding] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState('basic');
  const [pitches, setPitches] = useState([]);
  const [pitchNames, setPitchNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [capacityData, setCapacityData] = useState({});

  // Collapsible sections state
  const [showCapacityTable, setShowCapacityTable] = useState(false);
  const [capacityTableLoaded, setCapacityTableLoaded] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);

  // Heatmap section state
  const [showPitchHeatmap, setShowPitchHeatmap] = useState(false);
  const [heatmapLoaded, setHeatmapLoaded] = useState(false);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapMonth, setHeatmapMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [pitchConfigurations, setPitchConfigurations] = useState({});

  // NEW: Allocation Search section state
  const [showAllocationSearch, setShowAllocationSearch] = useState(false);
  const [allocationSearchLoaded, setAllocationSearchLoaded] = useState(false);
  const [allocationSearchLoading, setAllocationSearchLoading] = useState(false);
  const [searchDate, setSearchDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [searchPitch, setSearchPitch] = useState('all');
  const [searchResults, setSearchResults] = useState(null);
  const [searchTeamFilter, setSearchTeamFilter] = useState('all');
  const [sortField, setSortField] = useState('dateSort'); // default sort by date
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Analytics data state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDateRange, setAnalyticsDateRange] = useState(() => {
    const now = new Date();
    const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      start: `${lastWeek.getFullYear()}-${String(lastWeek.getMonth() + 1).padStart(2, '0')}-${String(lastWeek.getDate()).padStart(2, '0')}`,
      end: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    };
  });
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [teamsList, setTeamsList] = useState([]);

  // Multi-facility state
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('all');
  const [facilityPitchMap, setFacilityPitchMap] = useState({}); // Maps pitchId to facilityId

  // Feedback modal states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    from: '',
    topic: '',
    content: '',
	url: ''
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Premium modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [attemptedFeature, setAttemptedFeature] = useState('');

  // Monthly Report Generator state
  const [showReportGenerator, setShowReportGenerator] = useState(false);

  // Feedback topic options
  const feedbackTopics = [
    'Bug Report',
    'Feature Request',
    'User Interface Issue',
    'Performance Issue',
    'Data/Sync Problem',
    'Login/Authentication Issue',
    'Pitch Allocation Issue',
    'Capacity Outlook Issue',
    'Settings Issue',
    'General Feedback',
    'Other'
  ];

  // ---------- utils ----------
  const normalizePitchId = (pitchId) => {
    if (!pitchId) return '';
    const id = String(pitchId);
    // If it already starts with "Pitch " (display format), return as is
    if (id.startsWith('Pitch ')) return id;
    // If it has the pitch- format, convert to display format
    if (id.startsWith('pitch-')) {
      const num = id.replace('pitch-', '');
      return `Pitch ${num}`;
    }
    // If it's just "pitch1" format, convert to display format
    if (id.startsWith('pitch')) {
      const num = id.replace('pitch', '');
      return `Pitch ${num}`;
    }
    // If it's just a number, add Pitch prefix
    return `Pitch ${id}`;
  };

  // Check if user has access to reporting features
  const hasReportingAccess = subscriptionTier === 'standard' || subscriptionTier === 'premium';

  // Handle dropdown toggle with premium check
  const handleReportingToggle = (featureName, currentState, setState, setLoadedState) => {
    // Allow closing without check
    if (currentState) {
      setState(false);
      return;
    }

    // Check access when trying to open
    if (!hasReportingAccess) {
      setAttemptedFeature(featureName);
      setShowPremiumModal(true);
      return;
    }

    // Has access - open the section and force refresh
    setState(true);
    setLoadedState(false); // Force the useEffect to reload data
  };

  // local YYYY-MM-DD (avoids UTC shift from toISOString)
  const toLocalYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getPitchDisplayName = (pitchId, pitchNamesData) => {
    // pitchId can be: "1", "pitch-1", "Pitch 1", etc.
    // Extract the number first
    const pitchNum = String(pitchId).replace(/\D/g, '');
    
    // Try to find a custom name in pitchNames
    const possibleKeys = [
      `pitch-${pitchNum}`,
      `Pitch ${pitchNum}`,
      `pitch${pitchNum}`,
      `Pitch-${pitchNum}`,
      pitchNum,
    ];
    
    for (const key of possibleKeys) {
      if (pitchNamesData && pitchNamesData[key] && pitchNamesData[key].trim()) {
        return pitchNamesData[key];
      }
    }
    
    // Default to "Pitch X" if no custom name found
    return `Pitch ${pitchNum}`;
  };

  const getTeamDisplayName = (teamId) => {
    console.log('🔍 getTeamDisplayName called with:', teamId);
    console.log('🔍 teamsList available:', teamsList);
    
    // If teamId is already a display name (not a UUID), return it
    if (teamId && !teamId.startsWith('team_') && !teamId.startsWith('team-')) {
      // Check if it looks like a name (has spaces or doesn't match UUID pattern)
      if (teamId.includes(' ') || !/^team[_-]/.test(teamId)) {
        return teamId;
      }
    }
    
    // Find team in teamsList by ID or name
    const team = teamsList.find(t => {
      const match = t.id === teamId || t.teamId === teamId || t.name === teamId;
      if (match) console.log('✅ Found matching team:', t);
      return match;
    });
    
    if (team && team.name) {
      console.log('✅ Returning team name:', team.name);
      return team.name;
    }
    
    // If not found, return a cleaned version
    console.log('⚠️ Team not found, using fallback');
    if (teamId && (teamId.startsWith('team_') || teamId.startsWith('team-'))) {
      return `Team ${teamId.replace(/^team[_-]/, '').substring(0, 8)}`;
    }
    return teamId || 'Unknown Team';
  };

  // Heatmap helper functions
  const getDaysInMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  const getMonthDisplayName = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigateToPreviousMonth = () => {
    const [year, month] = heatmapMonth.split('-').map(Number);
    const prevDate = new Date(year, month - 2);
    setHeatmapMonth(`${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`);
    setHeatmapLoaded(false);
  };

  const navigateToNextMonth = () => {
    const [year, month] = heatmapMonth.split('-').map(Number);
    const nextDate = new Date(year, month);
    setHeatmapMonth(`${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`);
    setHeatmapLoaded(false);
  };

  const navigateToCurrentMonth = () => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (heatmapMonth !== currentMonth) {
      setHeatmapMonth(currentMonth);
      setHeatmapLoaded(false);
    }
  };

  const getHeatColor = (value, maxValue) => {
    if (maxValue === 0) return '#f3f4f6'; // Light gray for empty sections
    const intensity = value / maxValue;
    
    if (intensity === 0) return '#f3f4f6'; // Light gray for 0% - makes unused capacity visible
    if (intensity <= 0.2) return '#dcfce7';
    if (intensity <= 0.4) return '#86efac';
    if (intensity <= 0.6) return '#fbbf24';
    if (intensity <= 0.8) return '#fb923c';
    return '#ef4444';
  };

  // Helper to parse allocation key
  const parseAllocationKey = (key) => {
    if (!key) {
      return {
        timeToken: '',
        pitchToken: '',
        section: ''
      };
    }
    
    // Handle two possible formats:
    // Format 1 (standard): "timeToken_pitchToken_section" e.g., "14:30_pitch1_A"
    // Format 2 (with date): "YYYY-MM-DD-HH:MM-pitch-X-Section" e.g., "2025-11-16-08:00-pitch-1-A"
    
    // Check if it contains underscore (Format 1)
    if (key.includes('_')) {
      const parts = key.split('_');
      return {
        timeToken: parts[0] || '',
        pitchToken: parts[1] || '',
        section: parts[2] || ''
      };
    }
    
    // Format 2: YYYY-MM-DD-HH:MM-pitch-X-Section
    // When split by '-': ["2025", "11", "16", "08:00", "pitch", "1", "A"]
    const dashParts = key.split('-');
    
    // Check if this is a date-based key (starts with year)
    if (dashParts.length >= 7 && dashParts[0].length === 4 && !isNaN(dashParts[0])) {
      // Format: YYYY-MM-DD-HH:MM-pitch-X-Section
      // Parts: [0]=YYYY, [1]=MM, [2]=DD, [3]=HH:MM, [4]=pitch, [5]=X, [6]=Section
      return {
        timeToken: dashParts[3] || '', // HH:MM (e.g., "08:00")
        pitchToken: `${dashParts[4]}-${dashParts[5]}`, // pitch-X (e.g., "pitch-1")
        section: dashParts[6] || '' // Section letter (e.g., "A")
      };
    }
    
    // Fallback - treat as Format 1 but with dashes
    // This handles edge cases like "14:30-pitch1-A"
    if (dashParts.length === 3) {
      return {
        timeToken: dashParts[0] || '',
        pitchToken: dashParts[1] || '',
        section: dashParts[2] || ''
      };
    }
    
    // Last resort fallback
    console.warn('Unable to parse allocation key:', key);
    return {
      timeToken: '',
      pitchToken: '',
      section: ''
    };
  };

  // Helper to convert time string to timestamp for sorting
  const timeToTimestamp = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours * 60 + minutes) * 60 * 1000; // milliseconds from midnight
  };

  // NEW: Allocation Search Functions
  const performAllocationSearch = async () => {
    if (!clubId || !searchDate) {
      console.warn('Missing clubId or searchDate');
      return;
    }

    setAllocationSearchLoading(true);
    
    try {
      console.log('Searching for allocations:', { clubId, searchDate, searchPitch, searchTeamFilter });
      
      // Get all allocation documents from the new subcollection structure
      const allAllocations = {};
      
      // Build list of pitches to query - filter by selected facility if not 'all'
      let pitchesToQuery = searchPitch === 'all' ? pitches : [pitches.find(p => normalizePitchId(p.id) === normalizePitchId(searchPitch))].filter(Boolean);
      
      // Filter by facility if selected
      if (selectedFacilityId !== 'all' && facilities.length > 0) {
        pitchesToQuery = pitchesToQuery.filter(pitch => {
          const pitchId = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
          return facilityPitchMap[pitchId] === selectedFacilityId;
        });
      }
      
      console.log('Querying pitches:', pitchesToQuery);
      
      // Query each pitch's allocations subcollection
      for (const pitch of pitchesToQuery) {
        const pitchId = pitch.id || pitch;
        const pitchNum = String(pitchId).replace(/\D/g, '');
        
        // New path: clubs/{clubId}/allocations/pitch-{pitchNum}/items
        const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
        const allocationsSnapshot = await getDocs(allocationsRef);
        
        console.log(`Querying allocations for pitch-${pitchNum} on ${searchDate}`);
        console.log(`Found ${allocationsSnapshot.size} allocation documents for pitch-${pitchNum}`);
        
        // Filter by date and add to allAllocations
        allocationsSnapshot.forEach((allocationDoc) => {
          const docId = allocationDoc.id;
          const allocationData = allocationDoc.data();
          
          // Extract date from document ID (YYYY-MM-DD-HH:MM-pitch-X-Section)
          const parts = docId.split('-');
          if (parts.length >= 7) {
            const docDate = `${parts[0]}-${parts[1]}-${parts[2]}`; // YYYY-MM-DD
            
            if (docDate === searchDate) {
              // Use document ID as the key for consistency
              allAllocations[docId] = allocationData;
              console.log(`✅ Added allocation: ${docId}`, allocationData);
            } else {
              console.log(`⏭️ Skipped (date mismatch): ${docId}, date=${docDate} vs ${searchDate}`);
            }
          } else {
            console.warn(`⚠️ Unexpected document ID format: ${docId}`);
          }
        });
      }
      
      console.log('Total allocation keys found:', Object.keys(allAllocations).length);
      
      if (Object.keys(allAllocations).length === 0) {
        setSearchResults({
          date: searchDate,
          pitch: searchPitch,
          allocations: [],
          summary: {
            totalAllocations: 0,
            teams: [],
            timeslots: []
          }
        });
        setAllocationSearchLoading(false);
        return;
      }

      let filteredAllocations = [];
      const teamsSet = new Set();
      const timeslotsSet = new Set();

      // Filter allocations based on selected pitch and team
      // Filter allocations based on selected pitch and team
      Object.entries(allAllocations).forEach(([key, allocation]) => {
        if (typeof allocation !== 'object') return;
        
        // Extract data from allocation fields
        const timeToken = allocation.startTime || allocation.slot || '';
        const pitchToken = allocation.pitchId || allocation.pitch || '';
        const section = (allocation.sections && allocation.sections[0]) || allocation.section || '';
        const teamName = allocation.teamName || allocation.team || '';
        
        if (!timeToken || !pitchToken) {
          console.log('⚠️ Skipping allocation with missing data:', key, allocation);
          return;
        }
        
        // Filter by pitch if not 'all'
        if (searchPitch !== 'all') {
          const normalizedSearchPitch = normalizePitchId(searchPitch);
          const normalizedAllocPitch = normalizePitchId(pitchToken);
          if (normalizedAllocPitch !== normalizedSearchPitch) return;
        }

        // Filter by team if not 'all'
        if (searchTeamFilter !== 'all') {
          if (teamName !== searchTeamFilter) {
            console.log(`⏭️ Skipped allocation - team filter mismatch: "${teamName}" !== "${searchTeamFilter}"`);
            return;
          }
        }

        teamsSet.add(teamName);
        timeslotsSet.add(timeToken);

        filteredAllocations.push({
          key,
          time: timeToken,
          pitch: pitchToken,
          pitchDisplay: getPitchDisplayName(pitchToken, pitchNames),
          section,
          team: teamName, // Display the team name directly
          teamName: teamName, // Keep for filtering
          duration: allocation.duration || 'N/A',
          allocationType: allocation.allocationType || allocation.type || 'unknown'
        });
      });

      console.log('Filtered allocations before grouping:', filteredAllocations.length);

      // Group allocations by time + pitch + team
      const groupedMap = {};
      
      filteredAllocations.forEach((allocation) => {
        const groupKey = `${allocation.time}_${allocation.pitch}_${allocation.team}`;
        
        if (!groupedMap[groupKey]) {
          groupedMap[groupKey] = {
            time: allocation.time,
            pitch: allocation.pitch,
            pitchDisplay: allocation.pitchDisplay,
            team: allocation.team,
            duration: allocation.duration,
            sections: [],
            keys: []
          };
        }
        
        groupedMap[groupKey].sections.push(allocation.section);
        groupedMap[groupKey].keys.push(allocation.key);
      });

      // Convert grouped map to array with date formatting
      const groupedAllocations = Object.values(groupedMap).map(group => {
        // Format date as DD/MM/YY
        const dateObj = new Date(searchDate + 'T00:00:00');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = String(dateObj.getFullYear()).slice(-2);
        const formattedDate = `${day}/${month}/${year}`;
        
        return {
          key: group.keys[0], // Use first key as primary
          date: formattedDate,
          dateSort: searchDate, // Keep original for sorting
          time: group.time,
          pitch: group.pitch,
          pitchDisplay: group.pitchDisplay,
          sections: group.sections.sort().join(', '), // Sort sections alphabetically
          team: group.team,
          duration: typeof group.duration === 'number' ? `${group.duration} min` : group.duration
        };
      });

      console.log('Grouped allocations:', groupedAllocations.length);

      // Sort allocations: Date, Time, Pitch, Team
      groupedAllocations.sort((a, b) => {
        // Sort by date first
        const dateCompare = a.dateSort.localeCompare(b.dateSort);
        if (dateCompare !== 0) return dateCompare;
        
        // Then by time
        const timeA = timeToTimestamp(a.time);
        const timeB = timeToTimestamp(b.time);
        if (timeA !== timeB) return timeA - timeB;
        
        // Then by pitch
        const pitchCompare = a.pitch.localeCompare(b.pitch);
        if (pitchCompare !== 0) return pitchCompare;
        
        // Then by team
        return a.team.localeCompare(b.team);
      });

      setSearchResults({
        date: searchDate,
        pitch: searchPitch,
        allocations: groupedAllocations,
        summary: {
          totalAllocations: filteredAllocations.length,
          teams: Array.from(teamsSet).sort(),
          timeslots: Array.from(timeslotsSet).sort()
        }
      });

    } catch (error) {
      console.error('Error performing allocation search:', error);
      setSearchResults({
        date: searchDate,
        pitch: searchPitch,
        allocations: [],
        summary: {
          totalAllocations: 0,
          teams: [],
          timeslots: []
        },
        error: error.message
      });
    } finally {
      setAllocationSearchLoading(false);
    }
  };

  // Handle column sorting for allocation search results
  const handleSort = (field) => {
    let newDirection = 'asc';
    
    // If clicking the same field, toggle direction
    if (sortField === field) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    
    setSortField(field);
    setSortDirection(newDirection);
    
    if (!searchResults || !searchResults.allocations) return;
    
    // Create a sorted copy of allocations
    const sortedAllocations = [...searchResults.allocations].sort((a, b) => {
      let compareValue = 0;
      
      switch(field) {
        case 'date':
        case 'dateSort':
          compareValue = a.dateSort.localeCompare(b.dateSort);
          break;
        case 'time':
          compareValue = timeToTimestamp(a.time) - timeToTimestamp(b.time);
          break;
        case 'pitch':
          compareValue = a.pitchDisplay.localeCompare(b.pitchDisplay);
          break;
        case 'sections':
          compareValue = a.sections.localeCompare(b.sections);
          break;
        case 'team':
          compareValue = a.team.localeCompare(b.team);
          break;
        case 'duration':
          // Extract numeric value from duration string (e.g., "60 min" -> 60)
          const durationA = parseInt(a.duration) || 0;
          const durationB = parseInt(b.duration) || 0;
          compareValue = durationA - durationB;
          break;
        default:
          compareValue = 0;
      }
      
      return newDirection === 'asc' ? compareValue : -compareValue;
    });
    
    // Update search results with sorted data
    setSearchResults({
      ...searchResults,
      allocations: sortedAllocations
    });
  };

  // Toggle allocation search section
  const toggleAllocationSearch = () => {
    handleReportingToggle('Allocation Search', showAllocationSearch, setShowAllocationSearch, setAllocationSearchLoaded);
    
    // If we just opened it and it wasn't loaded, perform search
    if (!showAllocationSearch && !allocationSearchLoaded && hasReportingAccess) {
      performAllocationSearch();
    }
  };

  // Analytics helper functions
  const calculateSessionsFromAllocations = (allocations) => {
    // Group by team, date, and overlapping time periods to identify actual bookings/sessions
    const teamBookings = {};
    
    Object.entries(allocations).forEach(([key, allocation]) => {
      if (typeof allocation !== 'object' || !allocation.team) return;
      
      const { timeToken, pitchToken, section } = parseAllocationKey(key);
      // Get team name from the correct field
      const teamName = allocation.teamName || allocation.team || 'Unknown';
      
      // Extract date from the allocation key or use current processing date
      // For now, we'll group by team and time period within the same data processing cycle
      const timeKey = timeToken; // e.g., "14:30"
      
      if (!teamBookings[teamName]) {
        teamBookings[teamName] = {};
      }
      if (!teamBookings[teamName][timeKey]) {
        teamBookings[teamName][timeKey] = {
          sections: new Set(),
          pitches: new Set(),
          startTime: timeToTimestamp(timeToken),
          allocations: []
        };
      }
      
      teamBookings[teamName][timeKey].sections.add(section);
      teamBookings[teamName][timeKey].pitches.add(pitchToken);
      teamBookings[teamName][timeKey].allocations.push({
        time: timeToken,
        pitch: pitchToken,
        section: section,
        timestamp: timeToTimestamp(timeToken)
      });
    });
    
    // Convert bookings to sessions by merging consecutive time slots for the same team
    const sessions = [];
    
    Object.entries(teamBookings).forEach(([team, timeSlots]) => {
      // Sort time slots
      const sortedTimes = Object.keys(timeSlots).sort((a, b) => 
        timeToTimestamp(a) - timeToTimestamp(b)
      );
      
      let currentSession = null;
      
      sortedTimes.forEach(timeKey => {
        const booking = timeSlots[timeKey];
        const timestamp = booking.startTime;
        
        // If this is the first slot or there's a gap > 15 minutes, start a new session
        if (!currentSession || timestamp - currentSession.endTime > 15 * 60 * 1000) {
          // Finish previous session if exists
          if (currentSession) {
            sessions.push({
              team: currentSession.team,
              startTime: currentSession.startTime,
              endTime: currentSession.endTime,
              duration: (currentSession.endTime - currentSession.startTime) / (60 * 1000), // minutes
              sections: Array.from(currentSession.sections),
              pitches: Array.from(currentSession.pitches),
              totalAllocations: currentSession.totalAllocations
            });
          }
          
          // Start new session
          currentSession = {
            team,
            startTime: timestamp,
            endTime: timestamp + 15 * 60 * 1000, // 15 minutes
            sections: new Set(booking.sections),
            pitches: new Set(booking.pitches),
            totalAllocations: booking.allocations.length
          };
        } else {
          // Extend current session
          currentSession.endTime = timestamp + 15 * 60 * 1000;
          booking.sections.forEach(section => currentSession.sections.add(section));
          booking.pitches.forEach(pitch => currentSession.pitches.add(pitch));
          currentSession.totalAllocations += booking.allocations.length;
        }
      });
      
      // Don't forget the last session
      if (currentSession) {
        sessions.push({
          team: currentSession.team,
          startTime: currentSession.startTime,
          endTime: currentSession.endTime,
          duration: (currentSession.endTime - currentSession.startTime) / (60 * 1000), // minutes
          sections: Array.from(currentSession.sections),
          pitches: Array.from(currentSession.pitches),
          totalAllocations: currentSession.totalAllocations
        });
      }
    });
    
    return sessions;
  };
  
  const calculateAnalytics = async (startDate, endDate, filterTeam = 'all') => {
    if (!clubId || pitches.length === 0) return null;
    
    const allSessions = [];
    const filteredSessions = []; // For team-specific metrics
    const pitchUsageData = {};
    const filteredPitchUsageData = {}; // For team-specific metrics
    const timeSlotData = {};
    
    // Filter pitches by selected facility
    let pitchesToProcess = pitches;
    if (selectedFacilityId !== 'all' && facilities.length > 0) {
      pitchesToProcess = pitches.filter(pitch => {
        const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
        return facilityPitchMap[pitchKey] === selectedFacilityId;
      });
      console.log('📊 Filtered pitches for analytics:', pitchesToProcess.map(p => p.id));
    }
    
    // Initialize pitch data for both all teams and filtered teams
    pitchesToProcess.forEach(pitch => {
      const normalizedId = normalizePitchId(pitch.id);
      pitchUsageData[normalizedId] = {
        sessions: 0,
        totalDuration: 0,
        sectionUsage: {},
        timeDistribution: {}
      };
      filteredPitchUsageData[normalizedId] = {
        sessions: 0,
        totalDuration: 0,
        sectionUsage: {},
        timeDistribution: {}
      };
    });
    
    // Get data for date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(toLocalYMD(d));
    }
    
    for (const date of dates) {
      // Query each pitch's allocations subcollection
      for (const pitch of pitchesToProcess) {
        
        const pitchNum = String(pitch.id).replace(/\D/g, '');
        
        // New path: clubs/{clubId}/allocations/pitch-{pitchNum}/items
        const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
        const allocationsSnapshot = await getDocs(allocationsRef);
        
        // Filter allocations by date and process them
        const originalData = {};
        
        allocationsSnapshot.forEach((allocationDoc) => {
          const docId = allocationDoc.id;
          // Document ID format: YYYY-MM-DD-HH:MM-pitch-X-Section (7 parts when split by dash)
          
          // Extract date from document ID
          const parts = docId.split('-');
          if (parts.length >= 7) {
            const docDate = `${parts[0]}-${parts[1]}-${parts[2]}`; // YYYY-MM-DD
            
            // Only include allocations that match the current date in the loop
            if (docDate === date) {
              const allocationData = allocationDoc.data();
              originalData[docId] = allocationData;
            }
          }
        });
        
        // Skip if no allocations for this date
        if (Object.keys(originalData).length === 0) continue;
        
        const filteredData = {};
        
        // Always process all data for global analytics
        const allTeamSessions = calculateSessionsFromAllocations(originalData);
        allSessions.push(...allTeamSessions);
        
        // Filter by team for team-specific metrics
        Object.entries(originalData).forEach(([key, allocation]) => {
          if (typeof allocation !== 'object') return;
          
          // Get the team name from the allocation
          const allocationTeamName = allocation.teamName || allocation.team || '';
          
          // Compare team names (not IDs)
          if (filterTeam === 'all' || allocationTeamName === filterTeam) {
            filteredData[key] = allocation;
          }
        });
        
        const teamSpecificSessions = calculateSessionsFromAllocations(filteredData);
        filteredSessions.push(...teamSpecificSessions);
        
        // Track time slot usage (all teams)
        Object.entries(originalData).forEach(([key, allocation]) => {
          if (typeof allocation !== 'object') return;
          // Parse document ID to extract time, pitch, and section
          // Format: YYYY-MM-DD-HH:MM-pitch-X-Section
          // Parts:  [0]  [1] [2] [3]   [4]   [5] [6]
          const parts = key.split('-');
          if (parts.length >= 7) {
            const timeToken = parts[3]; // HH:MM
            const pitchToken = `${parts[4]}-${parts[5]}`; // pitch-X
            const section = parts[6]; // Section letter
            const normalizedPitch = normalizePitchId(pitchToken);
            
            if (!timeSlotData[timeToken]) timeSlotData[timeToken] = 0;
            timeSlotData[timeToken]++;
            
            if (pitchUsageData[normalizedPitch]) {
              if (!pitchUsageData[normalizedPitch].sectionUsage[section]) {
                pitchUsageData[normalizedPitch].sectionUsage[section] = 0;
              }
              pitchUsageData[normalizedPitch].sectionUsage[section]++;
              
              if (!pitchUsageData[normalizedPitch].timeDistribution[timeToken]) {
                pitchUsageData[normalizedPitch].timeDistribution[timeToken] = 0;
              }
              pitchUsageData[normalizedPitch].timeDistribution[timeToken]++;
            }
          }
        });
        
        // Track filtered data for team-specific metrics
        Object.entries(filteredData).forEach(([key, allocation]) => {
          if (typeof allocation !== 'object') return;
          // Parse document ID to extract time, pitch, and section
          // Format: YYYY-MM-DD-HH:MM-pitch-X-Section
          // Parts:  [0]  [1] [2] [3]   [4]   [5] [6]
          const parts = key.split('-');
          if (parts.length >= 7) {
            const timeToken = parts[3]; // HH:MM
            const pitchToken = `${parts[4]}-${parts[5]}`; // pitch-X
            const section = parts[6]; // Section letter
            const normalizedPitch = normalizePitchId(pitchToken);
            
            if (filteredPitchUsageData[normalizedPitch]) {
              if (!filteredPitchUsageData[normalizedPitch].sectionUsage[section]) {
                filteredPitchUsageData[normalizedPitch].sectionUsage[section] = 0;
              }
              filteredPitchUsageData[normalizedPitch].sectionUsage[section]++;
              
              if (!filteredPitchUsageData[normalizedPitch].timeDistribution[timeToken]) {
                filteredPitchUsageData[normalizedPitch].timeDistribution[timeToken] = 0;
              }
              filteredPitchUsageData[normalizedPitch].timeDistribution[timeToken]++;
            }
          }
        });
      } // Close pitch loop
    } // Close date loop
    
    // Calculate pitch-specific sessions for filtered data (team-specific)
    pitches.forEach(pitch => {
      const normalizedId = normalizePitchId(pitch.id);
      const pitchSessions = filteredSessions.filter(session => 
        session.pitches.some(sessionPitch => normalizePitchId(sessionPitch) === normalizedId)
      );
      
      filteredPitchUsageData[normalizedId].sessions = pitchSessions.length;
      filteredPitchUsageData[normalizedId].totalDuration = pitchSessions.reduce(
        (sum, session) => sum + session.duration, 0
      );
    });
    
    // Calculate pitch-specific sessions for all data (global analytics)
    pitches.forEach(pitch => {
      const normalizedId = normalizePitchId(pitch.id);
      const pitchSessions = allSessions.filter(session => 
        session.pitches.some(sessionPitch => normalizePitchId(sessionPitch) === normalizedId)
      );
      
      pitchUsageData[normalizedId].sessions = pitchSessions.length;
      pitchUsageData[normalizedId].totalDuration = pitchSessions.reduce(
        (sum, session) => sum + session.duration, 0
      );
    });
    
    // Team activity (filtered by team selection)
    const totalFilteredSessions = filteredSessions.length;
    const avgFilteredSessionLength = totalFilteredSessions > 0 
      ? Math.round(filteredSessions.reduce((sum, s) => sum + s.duration, 0) / totalFilteredSessions)
      : 0;
    
    // Global analytics (always all teams)
    const sortedTimeSlots = Object.entries(timeSlotData)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
    
    const underutilizedSlots = Object.entries(timeSlotData)
      .sort(([,a], [,b]) => a - b)
      .slice(0, 5);
    
    // Find overutilized sections per pitch (all teams)
    const overutilizedSections = {};
    const underutilizedSectionsData = {};
    
    Object.entries(pitchUsageData).forEach(([pitchId, data]) => {
      const sections = Object.entries(data.sectionUsage);
      if (sections.length > 0) {
        const maxUsage = Math.max(...sections.map(([,count]) => count));
        const minUsage = Math.min(...sections.map(([,count]) => count));
        
        overutilizedSections[pitchId] = sections
          .filter(([,count]) => count === maxUsage)
          .map(([section]) => ({ section, count: maxUsage }));
          
        underutilizedSectionsData[pitchId] = sections
          .filter(([,count]) => count === minUsage)
          .map(([section]) => ({ section, count: minUsage }));
      }
    });
    
    return {
      teamActivity: {
        totalSessions: totalFilteredSessions,
        avgSessionLength: avgFilteredSessionLength,
        pitchSessions: filteredPitchUsageData, // Team-specific
        selectedTeam: filterTeam
      },
      // All following data is from all teams
      peakTimes: sortedTimeSlots.map(([time, count]) => ({ time, count })),
      underutilizedSlots: underutilizedSlots.map(([time, count]) => ({ time, count })),
      overutilizedSections,
      underutilizedSections: underutilizedSectionsData,
      dateRange: { startDate, endDate }
    };
  };

  // Initialize to current week's Monday (local)
  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle feedback modal
  const openFeedbackModal = () => {
    setFeedbackForm({
      from: user?.email || '',
      topic: '',
      content: '',
	url: window.location.href
    });
    setShowFeedbackModal(true);
    setIsMobileMenuOpen(false);
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackForm({
      from: '',
      topic: '',
      content: '',
	url: ''
    });
  };

  const handleFeedbackInputChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

 const submitFeedback = async () => {
  if (!feedbackForm.from || !feedbackForm.topic || !feedbackForm.content) {
    alert('Please fill in all fields');
    return;
  }

  setIsSubmittingFeedback(true);

  try {
    const formData = new FormData();
    formData.append('form-name', 'feedback-form');
    formData.append('email', feedbackForm.from);
    formData.append('topic', feedbackForm.topic);
    formData.append('message', feedbackForm.content);
    formData.append('url', feedbackForm.url); // Add this line

    const response = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData).toString()
    });

    if (response.ok) {
      alert('Thank you! Your feedback has been sent successfully. We\'ll review it soon.');
      closeFeedbackModal();
    } else {
      throw new Error(`Server responded with status: ${response.status}`);
    }
  } catch (error) {
    console.error('Netlify Forms submission failed:', error);
    alert('Direct submission failed. Opening your email client as backup...');
    
    const subject = `Pitchallo Feedback: ${feedbackForm.topic}`;
    const body = `From: ${feedbackForm.from}
Topic: ${feedbackForm.topic}
URL: ${feedbackForm.url}

Message:
${feedbackForm.content}

---
Sent from Pitchallo Feedback Form`;

    const mailtoLink = `mailto:giannini000@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    try {
      window.location.href = mailtoLink;
      setTimeout(() => {
        alert('Your email client should have opened.');
      }, 1000);
      closeFeedbackModal();
    } catch (mailtoError) {
      console.error('Mailto also failed:', mailtoError);
      closeFeedbackModal();
    }
  } finally {
    setIsSubmittingFeedback(false);
  }
};

  // Load user and club data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || 'user');
            if (userData.clubId) {
              setClubId(userData.clubId);

              const clubDoc = await getDoc(doc(db, 'clubs', userData.clubId));
              if (clubDoc.exists()) {
                const clubData = clubDoc.data();
                const name = clubData.name || `Club ${userData.clubId}`;
                setClubName(name);
                
                // Load subscription tier
                const tier = clubData.subscriptionTier || 'basic';
                console.log('📊 Subscription tier loaded:', tier);
                setSubscriptionTier(tier);

                // Load teams list for team name resolution
                const teamsData = clubData.teams || [];
                console.log('👥 Loaded teams:', teamsData);
                setTeamsList(teamsData);

                // Load pitches - try multiple sources
                let pitchList = [];
                let pitchesWereSet = false;
                
                // PRIORITY METHOD 1: Load from club document's pitches array (most reliable)
                if (clubData.pitches && Array.isArray(clubData.pitches) && clubData.pitches.length > 0) {
                  console.log('🔍 Found clubData.pitches:', clubData.pitches.length, 'pitches');
                  pitchList = clubData.pitches.map((pitch, index) => {
                    const pitchNum = pitch.pitchNumber || pitch.id?.replace?.('pitch-', '') || String(index + 1);
                    return {
                      id: String(pitchNum).replace('pitch-', ''),
                      name: pitch.name || `Pitch ${pitchNum}`,
                      docId: `pitch-${String(pitchNum).replace('pitch-', '')}`
                    };
                  });
                  console.log('✅ Pitches loaded from clubData.pitches (PRIORITY METHOD):', pitchList);
                  setPitches(pitchList);
                  pitchesWereSet = true;
                }
                
                // Method 2: Load from satelliteConfig.pitchBoundaries
                if (!pitchesWereSet && clubData.satelliteConfig?.pitchBoundaries && clubData.satelliteConfig.pitchBoundaries.length > 0) {
                  console.log('🔍 Found satelliteConfig.pitchBoundaries:', clubData.satelliteConfig.pitchBoundaries);
                  console.log('🔍 Number of boundaries:', clubData.satelliteConfig.pitchBoundaries.length);
                  
                  pitchList = clubData.satelliteConfig.pitchBoundaries.map((boundary, index) => {
                    console.log(`🔍 Processing boundary ${index}:`, boundary);
                    
                    // Extract pitch number from id field (e.g., "pitch-1" -> "1")
                    let pitchNum = boundary.pitchNumber || boundary.id || `${index + 1}`;
                    console.log(`  - Original pitchNum/id: ${pitchNum}`);
                    
                    if (typeof pitchNum === 'string' && pitchNum.startsWith('pitch-')) {
                      pitchNum = pitchNum.replace('pitch-', '');
                    }
                    
                    console.log(`  - Final pitch number: ${pitchNum}`);
                    console.log(`  - Pitch name: ${boundary.name || `Pitch ${pitchNum}`}`);
                    
                    return {
                      id: pitchNum,
                      name: boundary.name || `Pitch ${pitchNum}`,
                    };
                  });
                  console.log('✅ Pitches loaded from satelliteConfig:', pitchList);
                  setPitches(pitchList);
                  pitchesWereSet = true;
                } else if (!pitchesWereSet) {
                  if (!clubData.satelliteConfig) {
                    console.log('⚠️ No satelliteConfig found at all');
                  } else if (!clubData.satelliteConfig.pitchBoundaries) {
                    console.log('⚠️ satelliteConfig exists but no pitchBoundaries');
                  } else if (clubData.satelliteConfig.pitchBoundaries.length === 0) {
                    console.log('⚠️ pitchBoundaries array is empty');
                  }
                  console.log('⚠️ No satelliteConfig found, trying to detect pitches from collection...');
                  
                  // Method 2: Query the pitches collection to see what exists
                  try {
                    const pitchesRef = collection(db, 'clubs', userData.clubId, 'pitches');
                    const pitchesSnapshot = await getDocs(pitchesRef);
                    
                    console.log(`📊 Found ${pitchesSnapshot.size} pitch documents in collection`);
                    
                    pitchesSnapshot.forEach((pitchDoc) => {
                      // Extract pitch number from document ID (e.g., "pitch-1" -> "1")
                      const pitchId = pitchDoc.id;
                      console.log(`   Found pitch document: ${pitchId}`);
                      const pitchNumber = pitchId.replace('pitch-', '');
                      pitchList.push({
                        id: pitchNumber, // Store as just the number (e.g., "1")
                        name: `Pitch ${pitchNumber}`,
                        docId: pitchId // Keep the full document ID for reference
                      });
                    });
                    
                    if (pitchList.length > 0) {
                      console.log('✅ Pitches loaded from collection:', pitchList);
                      setPitches(pitchList);
                      pitchesWereSet = true;
                    } else {
                      // Method 3: Try to detect pitches by checking if pitch document exists (regardless of allocations)
                      console.log('⚠️ No pitch documents found, trying manual detection...');
                      
                      // Try to detect up to 10 pitches
                      for (let i = 1; i <= 10; i++) {
                        try {
                          // Check if the pitch document exists (not just if it has allocations)
                          const pitchDocRef = doc(db, 'clubs', userData.clubId, 'pitches', `pitch-${i}`);
                          const pitchDocSnap = await getDoc(pitchDocRef);
                          
                          if (pitchDocSnap.exists()) {
                            // Pitch document exists, include it even if it has 0 allocations
                            console.log(`   ✅ Found pitch-${i} (pitch document exists)`);
                            pitchList.push({
                              id: `${i}`,
                              name: `Pitch ${i}`,
                              docId: `pitch-${i}`
                            });
                          } else {
                            // Also check if there are allocations in new path
                            const testAllocRef = collection(db, 'clubs', userData.clubId, 'allocations', `pitch-${i}`, 'items');
                            const testSnapshot = await getDocs(testAllocRef);
                            
                            if (testSnapshot.size > 0) {
                              console.log(`   ✅ Found pitch-${i} with ${testSnapshot.size} allocations`);
                              pitchList.push({
                                id: `${i}`,
                                name: `Pitch ${i}`,
                                docId: `pitch-${i}`
                              });
                            }
                          }
                        } catch (err) {
                          // Pitch doesn't exist, continue
                          console.log(`   ❌ pitch-${i} not found or no access`);
                        }
                      }
                      
                      if (pitchList.length > 0) {
                        console.log('✅ Pitches detected from allocations:', pitchList);
                        setPitches(pitchList);
                        pitchesWereSet = true;
                      } else {
                        // Method 4: Use a sensible default (most clubs have 1-3 pitches)
                        console.warn('⚠️ Could not detect pitches automatically, using default (1-3)');
                        pitchList = [
                          { id: '1', name: 'Pitch 1', docId: 'pitch-1' },
                          { id: '2', name: 'Pitch 2', docId: 'pitch-2' },
                          { id: '3', name: 'Pitch 3', docId: 'pitch-3' }
                        ];
                        setPitches(pitchList);
                        pitchesWereSet = true;
                      }
                    }
                  } catch (error) {
                    console.error('❌ Error loading pitches:', error);
                    // Emergency fallback
                    pitchList = [
                      { id: '1', name: 'Pitch 1', docId: 'pitch-1' }
                    ];
                    console.log('⚠️ Using emergency fallback: 1 pitch');
                    setPitches(pitchList);
                    pitchesWereSet = true;
                  }
                }
                
                // Log final pitch list (removed the problematic "add to 3 pitches" fallback)
                if (pitchList.length > 0) {
                  console.log('✅ Final pitch list:', pitchList);
                } else {
                  console.warn('⚠️ No pitches found for this club');
                }

              // After pitches are loaded, try to enhance with pitchNames from settings
              try {
                const settingsRef = doc(db, 'clubs', userData.clubId, 'settings', 'general');
                const settingsDoc = await getDoc(settingsRef);
                if (settingsDoc.exists()) {
                  const settingsData = settingsDoc.data();
                  console.log('Settings data loaded:', settingsData);
                  
                  // If we haven't loaded pitches yet but we have pitchNames, use those
                  if (!pitchesWereSet && settingsData.pitchNames) {
                    const nameKeys = Object.keys(settingsData.pitchNames);
                    console.log('📊 Creating pitches from pitchNames:', nameKeys);
                    
                    const pitchesFromNames = [];
                    nameKeys.forEach(key => {
                      const match = key.match(/\d+/);
                      if (match) {
                        const pitchNum = match[0];
                        const pitchName = settingsData.pitchNames[key];
                        // Only add if name is not empty or if we have no pitches at all
                        pitchesFromNames.push({
                          id: pitchNum,
                          name: pitchName && pitchName.trim() ? pitchName : `Pitch ${pitchNum}`,
                          docId: `pitch-${pitchNum}`
                        });
                      }
                    });
                    
                    if (pitchesFromNames.length > 0) {
                      console.log('✅ Pitches created from pitchNames:', pitchesFromNames);
                      setPitches(pitchesFromNames);
                    }
                  } else if (pitchesWereSet) {
                    // We already have pitches loaded, just log that we're using them
                    console.log('✅ Pitches already loaded, using existing list:', pitchList.length, 'pitches');
                  }
                  
                  if (settingsData.pitchNames) {
                    console.log('Pitch names:', settingsData.pitchNames);
                    setPitchNames(settingsData.pitchNames);
                  }
                  if (settingsData.branding) {
                    setBranding(settingsData.branding);
                  }
                  if (settingsData.clubName) setClubName(settingsData.clubName);
                  // Load pitch section configurations (for capacity calculations, not for pitch list)
                  if (settingsData.pitchSectionConfigurations) {
                    console.log('Pitch section configurations from Firebase:', settingsData.pitchSectionConfigurations);
                    console.log('Keys in configurations:', Object.keys(settingsData.pitchSectionConfigurations));
                    console.log('Sample config structure:', Object.values(settingsData.pitchSectionConfigurations)[0]);
                    setPitchConfigurations(settingsData.pitchSectionConfigurations);
                    console.log('✅ Pitch configurations set in state');
                  } else {
                    console.warn('⚠️ No pitchSectionConfigurations found in settings');
                  }
                  // Load teams list from settings
                  if (settingsData.teams) {
                    let teamsArray = [];
                    if (Array.isArray(settingsData.teams)) {
                      // Old array format - ensure each has an id
                      teamsArray = settingsData.teams.map((team, idx) => ({
                        ...team,
                        id: team.id || `team_legacy_${idx}`
                      }));
                    } else if (typeof settingsData.teams === 'object') {
                      // UUID-keyed object format - use key as id
                      teamsArray = Object.entries(settingsData.teams).map(([key, team]) => ({
                        ...team,
                        id: key,
                        name: team.name || key
                      }));
                    }
                    console.log('Teams loaded from settings:', teamsArray);
                    setTeamsList(teamsArray);
                  } else {
                    console.warn('No teams found in settings');
                  }
                }
              } catch (error) {
                console.error('Error loading settings:', error);
              }

              // Load facilities for multi-facility support
              try {
                const facilitiesRef = collection(db, 'clubs', userData.clubId, 'facilities');
                const facilitiesSnapshot = await getDocs(facilitiesRef);
                
                if (!facilitiesSnapshot.empty) {
                  const facilitiesData = [];
                  const pitchFacilityMap = {};
                  
                  facilitiesSnapshot.forEach((facilityDoc) => {
                    const facility = { id: facilityDoc.id, ...facilityDoc.data() };
                    facilitiesData.push(facility);
                    
                    // Map each pitch to its facility
                    if (facility.pitches && Array.isArray(facility.pitches)) {
                      facility.pitches.forEach(pitch => {
                        const pitchNum = pitch.pitchNumber || pitch.id?.replace?.('pitch-', '');
                        if (pitchNum) {
                          pitchFacilityMap[`pitch-${pitchNum}`] = facility.id;
                        }
                      });
                    }
                  });
                  
                  // Sort: primary first, then by name
                  facilitiesData.sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return (a.name || '').localeCompare(b.name || '');
                  });
                  
                  console.log('🏟️ Facilities loaded:', facilitiesData.length);
                  console.log('🏟️ Pitch-facility map:', pitchFacilityMap);
                  setFacilities(facilitiesData);
                  setFacilityPitchMap(pitchFacilityMap);
                  
                  // ALWAYS load pitches from facilities when facilities exist
                  // This ensures all facility pitches are included (overrides old pitch loading)
                  if (facilitiesData.length > 0) {
                    const allPitches = [];
                    facilitiesData.forEach(facility => {
                      if (facility.pitches && Array.isArray(facility.pitches)) {
                        facility.pitches.forEach(pitch => {
                          const pitchNum = pitch.pitchNumber || pitch.id?.replace?.('pitch-', '');
                          if (pitchNum) {
                            allPitches.push({
                              id: String(pitchNum),
                              name: pitch.name || `Pitch ${pitchNum}`,
                              docId: `pitch-${pitchNum}`,
                              facilityId: facility.id,
                              facilityName: facility.name
                            });
                          }
                        });
                      }
                    });
                    
                    if (allPitches.length > 0) {
                      console.log('✅ Pitches loaded from ALL facilities:', allPitches);
                      console.log('✅ Total pitches across all facilities:', allPitches.length);
                      setPitches(allPitches);
                    }
                  }
                } else {
                  console.log('🏟️ No facilities subcollection found (single-facility club)');
                }
              } catch (error) {
                console.error('Error loading facilities:', error);
              }
              }  // Close if (clubDoc.exists())
            }
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        }
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Calculate capacity for a specific pitch and date
  const calculatePitchCapacity = async (clubId, date, pitchNumber, isAM = true, pitchConfig = null) => {
    const normalizedPitchId = normalizePitchId(pitchNumber);
    const pitchNum = String(pitchNumber).replace(/\D/g, '');

    console.log(`🔍 Calculating capacity for ${normalizedPitchId} on ${date} (${isAM ? 'AM' : 'PM'})`);

    try {
      // New path: clubs/{clubId}/allocations/pitch-{pitchNum}/items
      const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
      const allocationsSnapshot = await getDocs(allocationsRef);
      
      console.log(`📄 Querying allocations for pitch-${pitchNum}`);
      console.log(`📊 Found ${allocationsSnapshot.size} allocation documents`);

      let allocationsCount = 0;

      // Define time slots
      const amSlots = [];
      const pmSlots = [];
      
      // AM: 8:00 to 16:45 (Match day hours)
      for (let h = 8; h < 17; h++) {
        for (let m = 0; m < 60; m += 15) {
          amSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        }
      }
      
      // PM: 17:00 to 21:15 (Training hours - ends at 21:15 not 21:00)
      for (let h = 17; h <= 21; h++) {
        for (let m = 0; m < 60; m += 15) {
          if (h === 21 && m > 15) break;
          pmSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        }
      }
      
      const relevantSlots = isAM ? amSlots : pmSlots;
      console.log(`⏰ Relevant time slots (${isAM ? 'AM' : 'PM'}):`, relevantSlots.length, 'slots');
      
      // Determine number of sections based on pitch configuration
      let sectionsCount = 8; // Default to 8 sections (full pitch)
      let sections = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
      
      if (pitchConfig && pitchConfig.sectionsCount) {
        sectionsCount = pitchConfig.sectionsCount;
        sections = sections.slice(0, sectionsCount);
        console.log(`📐 Pitch configuration found: ${sectionsCount} sections configured`);
      } else {
        console.log(`📐 No pitch configuration found, using default: ${sectionsCount} sections`);
      }
      
      const totalSlots = relevantSlots.length * sectionsCount;
      console.log(`📏 Total available slots: ${totalSlots} (${relevantSlots.length} times × ${sectionsCount} sections)`);

      const matchedAllocations = [];

      // Process each allocation document
      // Each document ID is in format: YYYY-MM-DD-HH:MM-pitch-X-Section
      allocationsSnapshot.forEach((allocationDoc) => {
        const docId = allocationDoc.id;
        const data = allocationDoc.data();
        
        console.log(`🔎 Processing allocation doc: ${docId}`);
        
        // Parse the document ID to extract date, time, pitch, and section
        const parsed = parseAllocationKey(docId);
        console.log(`   Parsed: time=${parsed.timeToken}, pitch=${parsed.pitchToken}, section=${parsed.section}`);
        
        // Extract date from the document ID (YYYY-MM-DD part)
        const docDate = docId.split('-').slice(0, 3).join('-');
        
        // Check if this allocation matches our date
        if (docDate !== date) {
          console.log(`   ❌ Skipping: date mismatch (${docDate} !== ${date})`);
          return; // Skip if not the right date
        }
        
        const timeToken = parsed.timeToken;
        const section = parsed.section;
        const normalizedAllocPitch = normalizePitchId(parsed.pitchToken);
        
        // Check if this allocation is for our pitch
        if (normalizedAllocPitch !== normalizedPitchId) {
          console.log(`   ❌ Skipping: pitch mismatch (${normalizedAllocPitch} !== ${normalizedPitchId})`);
          return; // Skip if not the right pitch
        }
        
        // Check if this time slot is in our relevant range (AM or PM)
        if (relevantSlots.includes(timeToken)) {
          // Check if the section is within configured sections
          if (sections.includes(section)) {
            allocationsCount++;
            matchedAllocations.push({ 
              docId, 
              timeToken, 
              section, 
              allocationType: data.allocationType || data.type || 'unknown' 
            });
            console.log(`   ✅ Matched allocation: ${timeToken} section ${section}`);
          } else {
            console.log(`   ⚠️ Skipping allocation in section ${section} (not in configured sections: ${sections.join(', ')})`);
          }
        } else {
          console.log(`   ❌ Skipping: time ${timeToken} not in relevant slots (${isAM ? 'AM' : 'PM'})`);
        }
      });

      console.log(`  ✅ Matched ${allocationsCount} allocations for ${normalizedPitchId} on ${date}:`, matchedAllocations);

      const usedPercentage = totalSlots > 0 ? Math.round((allocationsCount / totalSlots) * 100) : 0;
      console.log(`✅ Final result: ${allocationsCount}/${totalSlots} slots used = ${usedPercentage}%`);
      
      return usedPercentage;
    } catch (error) {
      console.error('❌ Error calculating capacity:', error);
      return 0;
    }
  };

  // Load capacity data when section is expanded
  useEffect(() => {
    if (!showCapacityTable || capacityTableLoaded) return;
    if (!clubId || !currentWeekStart || pitches.length === 0) {
      console.log('⏳ Waiting for required data... clubId:', !!clubId, 'currentWeekStart:', !!currentWeekStart, 'pitches:', pitches.length);
      return;
    }

    const loadCapacityData = async () => {
      setLoading(true);
      console.log('🚀 Loading capacity data for week starting:', toLocalYMD(currentWeekStart));
      console.log('🔧 Pitch configurations available:', pitchConfigurations);
      console.log('🔧 Number of configurations:', Object.keys(pitchConfigurations).length);
      console.log('🔧 Pitches to process:', pitches);
      
      try {
        const newCapacityData = {};
        const dates = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(currentWeekStart);
          d.setDate(d.getDate() + i);
          dates.push(toLocalYMD(d));
        }
        
        console.log('📅 Dates to query:', dates);
        console.log('🏟️ Selected facility:', selectedFacilityId);
        console.log('🏟️ Facility-pitch map:', facilityPitchMap);

        // Filter pitches by selected facility
        let pitchesToProcess = pitches;
        if (selectedFacilityId !== 'all' && facilities.length > 0) {
          pitchesToProcess = pitches.filter(pitch => {
            const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
            const belongsToFacility = facilityPitchMap[pitchKey] === selectedFacilityId;
            console.log(`  Pitch ${pitch.id} (${pitchKey}) belongs to facility: ${facilityPitchMap[pitchKey]}, selected: ${selectedFacilityId}, include: ${belongsToFacility}`);
            return belongsToFacility;
          });
          console.log('🏟️ Filtered pitches for facility:', pitchesToProcess.map(p => p.id));
        }

        // IMPORTANT: Always show all pitches, even if they have 0 allocations
        for (const pitch of pitchesToProcess) {
          console.log(`\n🎯 Processing ${pitch.name || pitch.id}...`);
          const normalizedPitchId = normalizePitchId(pitch.id);
          
          // Debug: Show available configurations
          console.log('🔍 Available pitch configurations:', Object.keys(pitchConfigurations));
          console.log('🔍 Looking for configuration key:', normalizedPitchId);
          console.log('🔍 All configurations:', pitchConfigurations);
          
          const pitchConfig = pitchConfigurations[normalizedPitchId];
          console.log('🔍 Found config for this pitch:', pitchConfig);
          
          // Initialize the pitch data object - this ensures the pitch appears in the table
          newCapacityData[pitch.id] = {};
          for (const date of dates) {
            const amCapacity = await calculatePitchCapacity(clubId, date, pitch.id, true, pitchConfig);
            const pmCapacity = await calculatePitchCapacity(clubId, date, pitch.id, false, pitchConfig);
            // Store the capacity data - even if it's 0%, it will be displayed
            newCapacityData[pitch.id][date] = { am: amCapacity, pm: pmCapacity };
            console.log(`  ${date}: AM=${amCapacity}%, PM=${pmCapacity}%`);
          }
        }

        console.log('✅ Capacity data loaded successfully (including pitches with 0%):', newCapacityData);
        setCapacityData(newCapacityData);
        setCapacityTableLoaded(true);
      } catch (error) {
        console.error('❌ Error loading capacity data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCapacityData();
  }, [clubId, currentWeekStart, pitches, showCapacityTable, capacityTableLoaded, selectedFacilityId, facilities, facilityPitchMap]);

  // Load pitch configurations for heatmap
  useEffect(() => {
    if (!showPitchHeatmap || !clubId) return;

    const loadPitchConfigs = async () => {
      try {
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          if (settings.pitchSectionConfigurations) {
            setPitchConfigurations(settings.pitchSectionConfigurations);
          }
        }
      } catch (error) {
        console.error('Error loading pitch configurations:', error);
      }
    };

    loadPitchConfigs();
  }, [showPitchHeatmap, clubId]);

  // Load heatmap data when section is expanded or month changes
  useEffect(() => {
    if (!showPitchHeatmap || heatmapLoaded) return;
    if (!clubId || pitches.length === 0) return;

    const loadHeatmapData = async () => {
      setHeatmapLoading(true);
      console.log('🔥 Loading heatmap data for month:', heatmapMonth);
      console.log('🔥 Available pitch configurations:', Object.keys(pitchConfigurations));
      
      try {
        const [year, month] = heatmapMonth.split('-').map(Number);
        const daysInMonth = getDaysInMonth(heatmapMonth);
        
        const heatmap = {};
        
        // Filter pitches by selected facility
        let pitchesToProcess = pitches;
        if (selectedFacilityId !== 'all' && facilities.length > 0) {
          pitchesToProcess = pitches.filter(pitch => {
            const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
            return facilityPitchMap[pitchKey] === selectedFacilityId;
          });
          console.log('🔥 Filtered pitches for heatmap:', pitchesToProcess.map(p => p.id));
        }
        
        // IMPORTANT: Initialize ALL pitches with their sections, even if they have 0 allocations
        pitchesToProcess.forEach(pitch => {
          const pitchId = normalizePitchId(pitch.id);
          console.log(`🔥 Normalizing pitch ${pitch.id} to ${pitchId}`);
          heatmap[pitchId] = {};
          
          const config = pitchConfigurations[pitchId];
          console.log(`🔥 Config for ${pitchId}:`, config);
          let sections = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
          
          if (config && config.sectionsCount) {
            sections = sections.slice(0, config.sectionsCount);
            console.log(`🔥 Using ${config.sectionsCount} sections for ${pitchId}:`, sections);
          } else {
            console.log(`🔥 No config found for ${pitchId}, using default 8 sections`);
          }
          
          // Initialize all sections to 0 - this ensures even unused sections show up
          sections.forEach(section => {
            heatmap[pitchId][section] = 0;
          });
        });

        // Query allocations for each pitch
        for (const pitch of pitchesToProcess) {
          
          const pitchNum = String(pitch.id).replace(/\D/g, '');
          
          // New path: clubs/{clubId}/allocations/pitch-{pitchNum}/items
          const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
          const allocationsSnapshot = await getDocs(allocationsRef);
          
          console.log(`🔥 Querying allocations for pitch-${pitchNum} in month ${heatmapMonth}`);
          console.log(`🔥 Found ${allocationsSnapshot.size} allocation documents`);
          
          // Filter and count allocations for the selected month
          allocationsSnapshot.forEach((allocationDoc) => {
            const docId = allocationDoc.id;
            // Document ID format: YYYY-MM-DD-HH:MM-pitch-X-Section (e.g., "2025-11-16-08:00-pitch-1-A")
            // When split by '-': [0]=YYYY, [1]=MM, [2]=DD, [3]=HH:MM, [4]=pitch, [5]=X, [6]=Section
            
            // Extract the date from the document ID
            const parts = docId.split('-');
            if (parts.length >= 7) {
              const docYear = parseInt(parts[0]);
              const docMonth = parseInt(parts[1]);
              
              // Only count allocations that match the selected year and month
              if (docYear === year && docMonth === month) {
                // Extract pitch and section from document ID
                const pitchToken = `${parts[4]}-${parts[5]}`; // e.g., "pitch-1"
                const section = parts[6]; // e.g., "A"
                
                const normalizedPitch = normalizePitchId(pitchToken);
                
                console.log(`🔥 Counting allocation: ${docId} -> pitch: ${normalizedPitch}, section: ${section}`);
                
                if (heatmap[normalizedPitch] && heatmap[normalizedPitch][section] !== undefined) {
                  heatmap[normalizedPitch][section]++;
                } else {
                  console.warn(`⚠️ Section ${section} not found in heatmap for ${normalizedPitch}`);
                }
              }
            } else {
              console.warn(`⚠️ Unexpected document ID format: ${docId} (${parts.length} parts)`);
            }
          });
        }

        // Calculate utilization percentage based on available capacity
        // For each section, calculate: (actual allocations / potential allocations) × 100
        
        // Available time slots per day:
        // Training: 17:00-21:00 = 4 hours × 4 slots/hour = 16 slots
        // Match: 08:00-17:00 = 9 hours × 4 slots/hour = 36 slots
        // Total potential per day per section = 52 slots
        const slotsPerDayPerSection = 52; // 16 training + 36 match
        
        const heatmapWithMaxValues = {};
        Object.entries(heatmap).forEach(([pitchId, sections]) => {
          const config = pitchConfigurations[pitchId];
          const sectionCount = config?.sectionsCount || 8;
          
          // Calculate max possible allocations for one section over the month
          const maxPossiblePerSection = daysInMonth * slotsPerDayPerSection;
          
          console.log(`🔥 Pitch ${pitchId} - Sections: ${sectionCount}, Max possible per section: ${maxPossiblePerSection}`);
          
          // Convert raw counts to utilization percentages
          const utilizationData = {};
          Object.entries(sections).forEach(([section, count]) => {
            const utilizationPercent = (count / maxPossiblePerSection) * 100;
            utilizationData[section] = utilizationPercent;
            console.log(`🔥   Section ${section}: ${count} allocations = ${utilizationPercent.toFixed(1)}% utilization`);
          });
          
          // For color scaling, use 100% as the max (full utilization)
          heatmapWithMaxValues[pitchId] = {
            data: utilizationData,
            rawCounts: sections, // Keep raw counts for display
            maxValue: 100 // Always 100% for consistent color scaling
          };
        });

        console.log('🔥 Heatmap data calculated with utilization percentages (including 0% sections):', heatmapWithMaxValues);

        setHeatmapData({ pitchData: heatmapWithMaxValues, daysInMonth });
        setHeatmapLoaded(true);
      } catch (error) {
        console.error('Error loading heatmap data:', error);
      } finally {
        setHeatmapLoading(false);
      }
    };

    loadHeatmapData();
  }, [showPitchHeatmap, heatmapLoaded, clubId, pitches, heatmapMonth, pitchConfigurations, selectedFacilityId, facilities, facilityPitchMap]);

  // Load analytics data when section is expanded
  useEffect(() => {
    if (!showAnalytics || analyticsLoaded) return;
    if (!clubId || pitches.length === 0) return;

    const loadAnalyticsData = async () => {
      setAnalyticsLoading(true);
      try {
        const data = await calculateAnalytics(analyticsDateRange.start, analyticsDateRange.end, selectedTeam);
        setAnalyticsData(data);
        setAnalyticsLoaded(true);
      } catch (error) {
        console.error('Error loading analytics data:', error);
        setAnalyticsData(null);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    loadAnalyticsData();
  }, [showAnalytics, analyticsLoaded, clubId, pitches, analyticsDateRange, selectedTeam]);

  // Load teams list from settings
  useEffect(() => {
    if (!showAnalytics || !clubId) return;

    const loadTeamsList = async () => {
      try {
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          if (settings.teams) {
            let teamsArray = [];
            
            // Handle both array and object formats
            if (Array.isArray(settings.teams)) {
              // Old array format - ensure each has an id
              teamsArray = settings.teams.map((team, idx) => {
                if (typeof team === 'string') {
                  return { id: `team_legacy_${idx}`, name: team };
                }
                return {
                  ...team,
                  id: team.id || `team_legacy_${idx}`,
                  name: team.name || team.team || String(team)
                };
              });
            } else if (typeof settings.teams === 'object') {
              // UUID-keyed object format - use key as id
              teamsArray = Object.entries(settings.teams).map(([key, team]) => ({
                ...team,
                id: key,
                name: team.name || key
              }));
            }
            
            setTeamsList(teamsArray);
          } else {
            setTeamsList([]);
          }
        }
      } catch (error) {
        console.error('Error loading teams list:', error);
        setTeamsList([]);
      }
    };

    loadTeamsList();
  }, [showAnalytics, clubId]);

  // Reset loaded flags when week changes
  useEffect(() => {
    setCapacityTableLoaded(false);
    setAnalyticsLoaded(false);
  }, [currentWeekStart]);

  // Reset heatmap loaded flag when month changes
  useEffect(() => {
    setHeatmapLoaded(false);
  }, [heatmapMonth]);

  const getTrafficLightColor = (percentage) => {
    if (percentage >= 90) return '#ef4444';
    if (percentage >= 70) return '#f97316';
    if (percentage >= 50) return '#eab308';
    return '#22c55e';
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const formatDateRange = () => {
    if (!currentWeekStart) return '';
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${currentWeekStart.toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', options)}`;
  };

  const getDayHeaders = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const headers = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(currentWeekStart);
      d.setDate(d.getDate() + i);
      headers.push({ day: days[i], date: d.getDate(), fullDate: toLocalYMD(d) });
    }
    return headers;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  // Pitch Heatmap Component with proper field markings
  const PitchHeatmap = ({ pitchId, normalizedPitchId, data, maxValue, rawCounts }) => {
    // Use the same pitch display name function as the capacity table
    const displayName = getPitchDisplayName(pitchId, pitchNames);
    
    const config = pitchConfigurations[normalizedPitchId];
    
    let layout = '8-sections';
    let sections = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    
    if (config) {
      if (config.sectionsCount === 4) {
        layout = '4-sections';
        sections = ['A', 'B', 'C', 'D'];
      } else if (config.sectionsCount === 2) {
        layout = '2-sections';
        sections = ['A', 'B'];
      } else if (config.sectionsCount === 1) {
        layout = '1-section';
        sections = ['A'];
      }
    }

    const renderPitchLayout = () => {
      const pitchStyle = {
        position: 'relative',
        width: '180px',
        height: '280px',
        backgroundColor: '#10b981',
        border: '3px solid white',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      };

      // Common field markings
      const centerLine = (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '0',
          right: '0',
          height: '2px',
          backgroundColor: 'white',
          opacity: 0.5,
          zIndex: 1
        }} />
      );

      const centerCircle = (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '60px',
          height: '60px',
          border: '2px solid white',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 0.5,
          zIndex: 1
        }} />
      );

      const topGoalArea = (
        <div style={{
          position: 'absolute',
          top: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100px',
          height: '40px',
          border: '2px solid white',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          opacity: 0.5,
          zIndex: 1
        }} />
      );

      const bottomGoalArea = (
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100px',
          height: '40px',
          border: '2px solid white',
          borderBottom: 'none',
          borderRadius: '8px 8px 0 0',
          opacity: 0.5,
          zIndex: 1
        }} />
      );

      if (layout === '8-sections') {
        return (
          <div style={pitchStyle}>
            {centerLine}
            {centerCircle}
            {topGoalArea}
            {bottomGoalArea}
            <div style={{
              position: 'absolute',
              inset: '0',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gridTemplateRows: 'repeat(4, 1fr)',
              gap: '1px',
              padding: '2px'
            }}>
              {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((section, idx) => {
                const rawCount = rawCounts ? (rawCounts[section] || 0) : 0;
                const utilizationPercent = data[section] || 0;
                return (
                <div
                  key={section}
                  style={{
                    backgroundColor: getHeatColor(utilizationPercent, maxValue),
                    opacity: 0.85,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    zIndex: 2
                  }}
                  title={`Section ${section}: ${rawCount} allocations (${utilizationPercent.toFixed(1)}% utilization)`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.zIndex = '10';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.85';
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.zIndex = '2';
                  }}
                >
                  <div>{section}</div>
                  <div style={{ fontSize: '10px', marginTop: '2px' }}>
                    {rawCount}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        );
      } else if (layout === '4-sections') {
        return (
          <div style={pitchStyle}>
            {centerLine}
            {centerCircle}
            {topGoalArea}
            {bottomGoalArea}
            <div style={{
              position: 'absolute',
              inset: '0',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gridTemplateRows: 'repeat(2, 1fr)',
              gap: '2px',
              padding: '2px'
            }}>
              {sections.map(section => {
                const rawCount = rawCounts ? (rawCounts[section] || 0) : 0;
                const utilizationPercent = data[section] || 0;
                return (
                <div
                  key={section}
                  style={{
                    backgroundColor: getHeatColor(utilizationPercent, maxValue),
                    opacity: 0.85,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    zIndex: 2
                  }}
                  title={`Section ${section}: ${rawCount} allocations (${utilizationPercent.toFixed(1)}% utilization)`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.zIndex = '10';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.85';
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.zIndex = '2';
                  }}
                >
                  <div>{section}</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    {rawCount}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        );
      } else if (layout === '2-sections') {
        return (
          <div style={pitchStyle}>
            {centerLine}
            {centerCircle}
            {topGoalArea}
            {bottomGoalArea}
            <div style={{
              position: 'absolute',
              inset: '0',
              display: 'grid',
              gridTemplateColumns: '1fr',
              gridTemplateRows: 'repeat(2, 1fr)',
              gap: '2px',
              padding: '2px'
            }}>
              {sections.map(section => {
                const rawCount = rawCounts ? (rawCounts[section] || 0) : 0;
                const utilizationPercent = data[section] || 0;
                return (
                <div
                  key={section}
                  style={{
                    backgroundColor: getHeatColor(utilizationPercent, maxValue),
                    opacity: 0.85,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    zIndex: 2
                  }}
                  title={`Section ${section}: ${rawCount} allocations (${utilizationPercent.toFixed(1)}% utilization)`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.zIndex = '10';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.85';
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.zIndex = '2';
                  }}
                >
                  <div>{section}</div>
                  <div style={{ fontSize: '16px', marginTop: '4px' }}>
                    {rawCount}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        );
      } else {
        // 1 section
        const rawCount = rawCounts ? (rawCounts.A || 0) : 0;
        const utilizationPercent = data.A || 0;
        return (
          <div style={pitchStyle}>
            {centerLine}
            {centerCircle}
            {topGoalArea}
            {bottomGoalArea}
            <div style={{
              position: 'absolute',
              inset: '0',
              backgroundColor: getHeatColor(utilizationPercent, maxValue),
              opacity: 0.85,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#1f2937',
              cursor: 'pointer',
              transition: 'all 0.2s',
              zIndex: 2
            }}
            title={`${rawCount} allocations (${utilizationPercent.toFixed(1)}% utilization)`}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.85';
            }}
            >
              <div>A</div>
              <div style={{ fontSize: '24px', marginTop: '8px' }}>
                {rawCount}
              </div>
            </div>
          </div>
        );
      }
    };

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
      }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#1f2937',
          margin: 0,
          textAlign: 'center'
        }}>
          {displayName}
        </h3>
        {renderPitchLayout()}
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          textAlign: 'center'
        }}>
          Total: {Object.values(rawCounts || {}).reduce((sum, val) => sum + val, 0)} allocations
        </div>
      </div>
    );
  };

  if (!currentWeekStart) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f9fafb' }}>
        <div>Loading...</div>
      </div>
    );
  }

  const dayHeaders = getDayHeaders();

  const SidebarContent = ({ isMobile = false }) => (
    <>
      <div style={{
        width: '100%',
        padding: '16px',
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer'
      }}
      onClick={() => handleNavigation('/menu')}
      >
        <img 
          src={logo}
          alt="Pitch Allocator"
          style={{
            width: '100%',
            maxWidth: '218px',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
      </div>
      
      {/* Branded Club Info Card */}
      <div style={{ padding: '12px' }}>
        <div style={{
          background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
        }}>
          {/* Club Logo + Name Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '6px'
          }}>
            {branding?.clubLogoUrl && (
              <img 
                src={branding.clubLogoUrl} 
                alt="Club Logo" 
                style={{
                  height: '58px',
                  width: '58px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  flexShrink: 0
                }}
              />
            )}
            <div style={{
              fontSize: '17px',
              fontWeight: '600',
              color: 'white',
              lineHeight: '1.3'
            }}>
              {clubName || 'Loading...'}
            </div>
          </div>
          <div style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '12px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {user?.email || 'Not logged in'}
          </div>
          <div style={{
            fontSize: '11px',
            color: 'white',
            padding: '4px 10px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '20px',
            display: 'inline-block',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
          </div>
        </div>
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <button
          onClick={() => handleNavigation('/menu')}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Home Menu
        </button>

        <button
          onClick={() => handleNavigation('/club-pitch-map')}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Club Map
        </button>

        <AdminOnly><button
          onClick={() => handleNavigation('/settings')}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Settings
        </button></AdminOnly>

        <div style={{ 
          height: '1px', 
          backgroundColor: 'rgba(255,255,255,0.1)', 
          margin: '4px 0' 
        }} />

        <button
          onClick={openFeedbackModal}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Feedback
        </button>

        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Logout
        </button>
      </div>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', fontFamily: 'system-ui, sans-serif', display: 'flex', gap: '0' }}>
      {/* Desktop Sidebar */}
      <div style={{ 
        width: '250px', 
        flexShrink: 0, 
        backgroundColor: '#243665', 
        height: '100vh', 
        position: 'sticky', 
        top: 0, 
        left: 0, 
        display: 'flex', 
        flexDirection: 'column'
      }}
      className="desktop-sidebar"
      >
        <SidebarContent />
      </div>

      {/* Mobile Overlay Menu */}
      {isMobileMenuOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000
        }}
        className="mobile-menu-overlay"
        onClick={() => setIsMobileMenuOpen(false)}
        >
          <div style={{
            width: '280px',
            height: '100vh',
            backgroundColor: '#243665',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                zIndex: 1001,
                padding: '4px'
              }}
            >
              <X size={24} />
            </button>
            <SidebarContent isMobile={true} />
          </div>
        </div>
      )}

      {/* Main Content */}

      <div style={{ flex: 1, padding: '12px 16px 24px', overflowX: 'auto' }}>
        {/* Mobile Header */}
        <div className="mobile-header" style={{ 
          display: 'none',
          marginBottom: '16px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ width: '40px' }} />
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
            Facility utilisation Reporting
          </h1>
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#1f2937'
            }}
          >
            <Menu size={24} />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="desktop-header" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Capacity Outlook</h1>
        </div>

        {/* Facility Selector - Apple-style Segmented Control */}
        {facilities.length > 1 && (
          <div style={{
            marginBottom: '24px',
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px 20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '12px'
            }}>
              Filter by Location
            </div>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {/* All Facilities Option */}
              <button
                onClick={() => {
                  setSelectedFacilityId('all');
                  // Reset any loaded data to force reload
                  setCapacityData({});
                  setHeatmapData(null);
                  setAnalyticsData(null);
                  setSearchResults(null);
                  setCapacityTableLoaded(false);
                  setHeatmapLoaded(false);
                  setAnalyticsLoaded(false);
                  setAllocationSearchLoaded(false);
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: selectedFacilityId === 'all' ? '#ffffff' : '#374151',
                  backgroundColor: selectedFacilityId === 'all' ? '#243665' : '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: selectedFacilityId === 'all' ? '0 2px 4px rgba(36, 54, 101, 0.3)' : 'none'
                }}
              >
                All Locations
              </button>
              
              {/* Individual Facilities */}
              {facilities.map((facility) => {
                const isSelected = selectedFacilityId === facility.id;
                return (
                  <button
                    key={facility.id}
                    onClick={() => {
                      setSelectedFacilityId(facility.id);
                      // Reset any loaded data to force reload
                      setCapacityData({});
                      setHeatmapData(null);
                      setAnalyticsData(null);
                      setSearchResults(null);
                      setCapacityTableLoaded(false);
                      setHeatmapLoaded(false);
                      setAnalyticsLoaded(false);
                      setAllocationSearchLoaded(false);
                    }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: isSelected ? '#ffffff' : '#374151',
                      backgroundColor: isSelected ? '#243665' : '#f3f4f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: isSelected ? '0 2px 4px rgba(36, 54, 101, 0.3)' : 'none'
                    }}
                  >
                    {facility.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* NEW SECTION: Allocation Search */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <button
              onClick={toggleAllocationSearch}
              style={{
                width: '100%',
                padding: '20px',
                backgroundColor: 'white',
                border: 'none',
                borderBottom: showAllocationSearch ? '1px solid #e5e7eb' : 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!showAllocationSearch) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Search size={24} color="#3b82f6" />
                <span>🔍 Allocation Search</span>
              </div>
              {showAllocationSearch ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
            </button>

            {showAllocationSearch && (
              <div style={{
                padding: '24px',
                animation: 'slideIn 0.3s ease-out'
              }}>
                {/* Search Controls */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  marginBottom: '20px'
                }}>
                  {/* Date Selector */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '6px'
                    }}>
                      Date
                    </label>
                    <input
                      type="date"
                      value={searchDate}
                      onChange={(e) => setSearchDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Pitch Selector */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '6px'
                    }}>
                      Pitch
                    </label>
                    <select
                      value={searchPitch}
                      onChange={(e) => setSearchPitch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="all">All Pitches</option>
                      {pitches
                        .filter(pitch => {
                          if (selectedFacilityId === 'all' || facilities.length === 0) return true;
                          const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
                          return facilityPitchMap[pitchKey] === selectedFacilityId;
                        })
                        .map((pitch) => {
                        const pitchId = pitch.id || pitch;
                        const normalizedPitchId = normalizePitchId(pitchId);
                        const pitchNum = String(pitchId).replace(/\D/g, '');
                        return (
                          <option key={pitchId} value={`pitch${pitchNum}`}>
                            {getPitchDisplayName(pitchId, pitchNames)}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Team Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '6px'
                    }}>
                      Team
                    </label>
                    <select
                      value={searchTeamFilter}
                      onChange={(e) => setSearchTeamFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="all">All Teams</option>
                      {teamsList.map((team, index) => {
                        // Handle both string and object formats
                        const teamName = typeof team === 'string' ? team : team.name || String(team);
                        const teamKey = typeof team === 'string' ? team : team.name || index;
                        return (
                          <option key={teamKey} value={teamName}>
                            {teamName}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Search Button */}
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      onClick={performAllocationSearch}
                      disabled={allocationSearchLoading}
                      style={{
                        width: '100%',
                        padding: '10px 20px',
                        backgroundColor: allocationSearchLoading ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: allocationSearchLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Search size={16} />
                      {allocationSearchLoading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </div>

                {/* Search Results */}
                {searchResults && (
                  <div>
                    {/* Summary Card */}
                    <div style={{
                      padding: '20px',
                      backgroundColor: '#eff6ff',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <h3 style={{
                        margin: '0 0 16px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#1e40af'
                      }}>
                        📋 Summary for {new Date(searchResults.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </h3>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '16px'
                      }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            Total Allocations
                          </div>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                            {searchResults.summary.totalAllocations}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            Teams Involved
                          </div>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                            {searchResults.summary.teams.length}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            Time Slots Used
                          </div>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                            {searchResults.summary.timeslots.length}
                          </div>
                        </div>
                      </div>

                      {searchResults.summary.teams.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                            Teams:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {searchResults.summary.teams.map((team) => (
                              <span
                                key={team}
                                style={{
                                  padding: '4px 12px',
                                  backgroundColor: '#dbeafe',
                                  color: '#1e40af',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}
                              >
                                {team}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Allocations Table */}
                    {searchResults.allocations.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f9fafb' }}>
                              <th 
                                onClick={() => handleSort('dateSort')}
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  borderBottom: '2px solid #e5e7eb', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Date
                                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                    {sortField === 'dateSort' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                                  </span>
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('time')}
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  borderBottom: '2px solid #e5e7eb', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Time
                                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                    {sortField === 'time' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                                  </span>
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('pitch')}
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  borderBottom: '2px solid #e5e7eb', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Pitch
                                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                    {sortField === 'pitch' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                                  </span>
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('sections')}
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  borderBottom: '2px solid #e5e7eb', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Sections
                                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                    {sortField === 'sections' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                                  </span>
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('team')}
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  borderBottom: '2px solid #e5e7eb', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Team
                                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                    {sortField === 'team' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                                  </span>
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('duration')}
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  borderBottom: '2px solid #e5e7eb', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Duration
                                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                    {sortField === 'duration' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                                  </span>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {searchResults.allocations.map((allocation, index) => (
                              <tr
                                key={allocation.key}
                                style={{
                                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                                  borderBottom: '1px solid #e5e7eb'
                                }}
                              >
                                <td style={{ padding: '12px', color: '#1f2937' }}>
                                  {allocation.date}
                                </td>
                                <td style={{ padding: '12px', color: '#1f2937' }}>
                                  {allocation.time}
                                </td>
                                <td style={{ padding: '12px', color: '#1f2937', fontWeight: '500' }}>
                                  {allocation.pitchDisplay}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <span style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#dbeafe',
                                    color: '#1e40af',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '500'
                                  }}>
                                    {allocation.sections}
                                  </span>
                                </td>
                                <td style={{ padding: '12px', color: '#1f2937', fontWeight: '500' }}>
                                  {allocation.team}
                                </td>
                                <td style={{ padding: '12px', color: '#6b7280' }}>
                                  {allocation.duration}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#6b7280',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px'
                      }}>
                        <Search size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                        <div style={{ fontSize: '16px', fontWeight: '500' }}>
                          No allocations found
                        </div>
                        <div style={{ fontSize: '14px', marginTop: '8px' }}>
                          Try adjusting your search criteria
                        </div>
                      </div>
                    )}

                    {searchResults.error && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        color: '#dc2626',
                        fontSize: '14px',
                        marginTop: '16px'
                      }}>
                        Error: {searchResults.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* SECTION 1: Weekly Capacity Overview */}
        <div style={{ marginBottom: '20px' }}>
          <div 
            onClick={() => handleReportingToggle('Weekly Capacity Overview', showCapacityTable, setShowCapacityTable, setCapacityTableLoaded)}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '16px 20px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!showCapacityTable) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📅</span>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Weekly Capacity Overview
              </h2>
              {showCapacityTable && capacityTableLoaded && (
                <span style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  {(() => {
                    if (selectedFacilityId === 'all' || facilities.length === 0) {
                      return pitches.length;
                    }
                    return pitches.filter(p => {
                      const pitchKey = `pitch-${String(p.id).replace(/\D/g, '')}`;
                      return facilityPitchMap[pitchKey] === selectedFacilityId;
                    }).length;
                  })()} pitches
                </span>
              )}
            </div>
            <span style={{
              fontSize: '20px',
              color: '#6b7280',
              transform: showCapacityTable ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}>
              ▼
            </span>
          </div>

          {showCapacityTable && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              marginTop: '8px',
              animation: 'slideIn 0.3s ease'
            }}>
              {/* Week Navigation Bar */}
              <div style={{ 
                padding: '16px 20px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1', minWidth: '200px' }}>
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f3f4f6', 
                    borderRadius: '6px', 
                    fontWeight: '600', 
                    fontSize: '13px', 
                    color: '#1f2937',
                    textAlign: 'center',
                    flex: '1'
                  }}>
                    {formatDateRange()}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={goToPreviousWeek}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      padding: '8px 10px', 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '6px', 
                      cursor: 'pointer', 
                      fontSize: '12px', 
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <ChevronLeft size={14} />
                    <span className="desktop-text">Previous 7 Days</span>
                    <span className="mobile-text" style={{ display: 'none' }}>Prev</span>
                  </button>
                  <button
                    onClick={goToNextWeek}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      padding: '8px 10px', 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '6px', 
                      cursor: 'pointer', 
                      fontSize: '12px', 
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <span className="desktop-text">Next 7 Days</span>
                    <span className="mobile-text" style={{ display: 'none' }}>Next</span>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {/* Capacity Legend */}
              <div style={{ 
                padding: '12px 20px',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                display: 'flex', 
                gap: '16px', 
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Capacity Legend:</span>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#22c55e' }} />
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>≤50%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#eab308' }} />
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>50-70%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#f97316' }} />
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>70-90%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>90%+</span>
                  </div>
                </div>
              </div>

              {loading ? (
                <div style={{ 
                  padding: '48px', 
                  textAlign: 'center', 
                  color: '#6b7280' 
                }}>
                  <div style={{
                    display: 'inline-block',
                    animation: 'spin 1s linear infinite',
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e5e7eb',
                    borderTop: '3px solid #3b82f6',
                    borderRadius: '50%',
                    marginBottom: '12px'
                  }}></div>
                  <div>Loading capacity data...</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                    <thead>
                      <tr>
                        <th style={{ 
                          padding: '12px 8px', 
                          textAlign: 'left', 
                          backgroundColor: '#f9fafb', 
                          borderBottom: '2px solid #e5e7eb', 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#374151',
                          minWidth: '80px',
                          position: 'sticky',
                          left: 0,
                          zIndex: 10
                        }}>
                          Pitch
                        </th>
                        {dayHeaders.map((header) => (
                          <th key={header.fullDate} colSpan="2" style={{ 
                            padding: '12px 4px', 
                            textAlign: 'center', 
                            backgroundColor: '#f9fafb', 
                            borderBottom: '2px solid #e5e7eb', 
                            borderLeft: '1px solid #e5e7eb', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#374151',
                            minWidth: '60px'
                          }}>
                            <div>{header.day}</div>
                            <div style={{ fontSize: '11px', fontWeight: '400', color: '#6b7280' }}>{header.date}</div>
                          </th>
                        ))}
                      </tr>
                      <tr>
                        <th style={{ 
                          padding: '8px', 
                          backgroundColor: '#f9fafb', 
                          borderBottom: '1px solid #e5e7eb', 
                          fontSize: '12px', 
                          fontWeight: '500', 
                          color: '#6b7280',
                          position: 'sticky',
                          left: 0,
                          zIndex: 10
                        }}></th>
                        {dayHeaders.map((header) => (
                          <React.Fragment key={`${header.fullDate}-periods`}>
                            <th style={{ 
                              padding: '6px', 
                              textAlign: 'center', 
                              backgroundColor: '#f9fafb', 
                              borderBottom: '1px solid #e5e7eb', 
                              borderLeft: '1px solid #e5e7eb', 
                              fontSize: '10px', 
                              fontWeight: '500', 
                              color: '#6b7280' 
                            }}>AM</th>
                            <th style={{ 
                              padding: '6px', 
                              textAlign: 'center', 
                              backgroundColor: '#f9fafb', 
                              borderBottom: '1px solid #e5e7eb', 
                              borderLeft: '1px solid #e5e7eb', 
                              fontSize: '10px', 
                              fontWeight: '500', 
                              color: '#6b7280' 
                            }}>PM</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Render pitches - grouped by facility when "All" selected, filtered when specific facility selected */}
                      {(() => {
                        // If specific facility selected, just show those pitches
                        if (selectedFacilityId !== 'all' && facilities.length > 0) {
                          const filteredPitches = pitches.filter(pitch => {
                            const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
                            return facilityPitchMap[pitchKey] === selectedFacilityId;
                          });
                          
                          return filteredPitches.map((pitch, index) => {
                            const displayName = getPitchDisplayName(pitch.id, pitchNames);
                            return (
                              <tr key={pitch.id}>
                                <td style={{ 
                                  padding: '12px 8px', 
                                  borderBottom: index === filteredPitches.length - 1 ? 'none' : '1px solid #e5e7eb', 
                                  fontSize: '13px', 
                                  fontWeight: '500', 
                                  color: '#1f2937',
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: 'white',
                                  zIndex: 5,
                                  minWidth: '80px'
                                }}>
                                  {displayName}
                                </td>
                                {dayHeaders.map((header) => {
                                  const amCapacity = capacityData[pitch.id]?.[header.fullDate]?.am || 0;
                                  const pmCapacity = capacityData[pitch.id]?.[header.fullDate]?.pm || 0;
                                  return (
                                    <React.Fragment key={`${pitch.id}-${header.fullDate}`}>
                                      <td
                                        style={{ 
                                          padding: '6px', 
                                          textAlign: 'center', 
                                          borderBottom: index === filteredPitches.length - 1 ? 'none' : '1px solid #e5e7eb', 
                                          borderLeft: '1px solid #e5e7eb', 
                                          cursor: 'pointer' 
                                        }}
                                        onClick={() => navigate(`/allocator/${pitch.id}?date=${header.fullDate}`)}
                                        title={`Click to view allocations for ${displayName} on ${header.fullDate}`}
                                      >
                                        <div
                                          style={{ 
                                            width: '28px', 
                                            height: '28px', 
                                            borderRadius: '50%', 
                                            backgroundColor: getTrafficLightColor(amCapacity), 
                                            margin: '0 auto', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', 
                                            transition: 'transform 0.2s ease' 
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                          <span style={{ fontSize: '9px', color: 'white', fontWeight: '600' }}>{Math.round(amCapacity)}%</span>
                                        </div>
                                      </td>
                                      <td
                                        style={{ 
                                          padding: '6px', 
                                          textAlign: 'center', 
                                          borderBottom: index === filteredPitches.length - 1 ? 'none' : '1px solid #e5e7eb', 
                                          borderLeft: '1px solid #e5e7eb', 
                                          cursor: 'pointer' 
                                        }}
                                        onClick={() => navigate(`/allocator/${pitch.id}?date=${header.fullDate}`)}
                                        title={`Click to view allocations for ${displayName} on ${header.fullDate}`}
                                      >
                                        <div
                                          style={{ 
                                            width: '28px', 
                                            height: '28px', 
                                            borderRadius: '50%', 
                                            backgroundColor: getTrafficLightColor(pmCapacity), 
                                            margin: '0 auto', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', 
                                            transition: 'transform 0.2s ease' 
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                          <span style={{ fontSize: '9px', color: 'white', fontWeight: '600' }}>{Math.round(pmCapacity)}%</span>
                                        </div>
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            );
                          });
                        }
                        
                        // "All Locations" selected - group by facility
                        if (facilities.length > 0) {
                          return facilities.map((facility) => {
                            const facilityPitches = pitches.filter(pitch => {
                              const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
                              return facilityPitchMap[pitchKey] === facility.id;
                            });
                            
                            if (facilityPitches.length === 0) return null;
                            
                            return (
                              <React.Fragment key={facility.id}>
                                {/* Facility Header Row */}
                                <tr>
                                  <td 
                                    colSpan={1 + dayHeaders.length * 2}
                                    style={{
                                      padding: '10px 12px',
                                      backgroundColor: '#f8fafc',
                                      borderBottom: '1px solid #e5e7eb',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      color: '#475569',
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 5
                                    }}
                                  >
                                    {facility.name}
                                  </td>
                                </tr>
                                {/* Facility Pitches */}
                                {facilityPitches.map((pitch, index) => {
                                  const displayName = getPitchDisplayName(pitch.id, pitchNames);
                                  return (
                                    <tr key={pitch.id}>
                                      <td style={{ 
                                        padding: '12px 8px', 
                                        borderBottom: '1px solid #e5e7eb', 
                                        fontSize: '13px', 
                                        fontWeight: '500', 
                                        color: '#1f2937',
                                        position: 'sticky',
                                        left: 0,
                                        backgroundColor: 'white',
                                        zIndex: 5,
                                        minWidth: '80px',
                                        paddingLeft: '24px' // Indent under facility
                                      }}>
                                        {displayName}
                                      </td>
                                      {dayHeaders.map((header) => {
                                        const amCapacity = capacityData[pitch.id]?.[header.fullDate]?.am || 0;
                                        const pmCapacity = capacityData[pitch.id]?.[header.fullDate]?.pm || 0;
                                        return (
                                          <React.Fragment key={`${pitch.id}-${header.fullDate}`}>
                                            <td
                                              style={{ 
                                                padding: '6px', 
                                                textAlign: 'center', 
                                                borderBottom: '1px solid #e5e7eb', 
                                                borderLeft: '1px solid #e5e7eb', 
                                                cursor: 'pointer' 
                                              }}
                                              onClick={() => navigate(`/allocator/${pitch.id}?date=${header.fullDate}`)}
                                              title={`Click to view allocations for ${displayName} on ${header.fullDate}`}
                                            >
                                              <div
                                                style={{ 
                                                  width: '28px', 
                                                  height: '28px', 
                                                  borderRadius: '50%', 
                                                  backgroundColor: getTrafficLightColor(amCapacity), 
                                                  margin: '0 auto', 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  justifyContent: 'center', 
                                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', 
                                                  transition: 'transform 0.2s ease' 
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                              >
                                                <span style={{ fontSize: '9px', color: 'white', fontWeight: '600' }}>{Math.round(amCapacity)}%</span>
                                              </div>
                                            </td>
                                            <td
                                              style={{ 
                                                padding: '6px', 
                                                textAlign: 'center', 
                                                borderBottom: '1px solid #e5e7eb', 
                                                borderLeft: '1px solid #e5e7eb', 
                                                cursor: 'pointer' 
                                              }}
                                              onClick={() => navigate(`/allocator/${pitch.id}?date=${header.fullDate}`)}
                                              title={`Click to view allocations for ${displayName} on ${header.fullDate}`}
                                            >
                                              <div
                                                style={{ 
                                                  width: '28px', 
                                                  height: '28px', 
                                                  borderRadius: '50%', 
                                                  backgroundColor: getTrafficLightColor(pmCapacity), 
                                                  margin: '0 auto', 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  justifyContent: 'center', 
                                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', 
                                                  transition: 'transform 0.2s ease' 
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                              >
                                                <span style={{ fontSize: '9px', color: 'white', fontWeight: '600' }}>{Math.round(pmCapacity)}%</span>
                                              </div>
                                            </td>
                                          </React.Fragment>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          });
                        }
                        
                        // No facilities - just show all pitches (legacy/single facility mode)
                        return pitches.map((pitch, index) => {
                          const displayName = getPitchDisplayName(pitch.id, pitchNames);
                          return (
                            <tr key={pitch.id}>
                              <td style={{ 
                                padding: '12px 8px', 
                                borderBottom: index === pitches.length - 1 ? 'none' : '1px solid #e5e7eb', 
                                fontSize: '13px', 
                                fontWeight: '500', 
                                color: '#1f2937',
                                position: 'sticky',
                                left: 0,
                                backgroundColor: 'white',
                                zIndex: 5,
                                minWidth: '80px'
                              }}>
                                {displayName}
                              </td>
                              {dayHeaders.map((header) => {
                                const amCapacity = capacityData[pitch.id]?.[header.fullDate]?.am || 0;
                                const pmCapacity = capacityData[pitch.id]?.[header.fullDate]?.pm || 0;
                                return (
                                  <React.Fragment key={`${pitch.id}-${header.fullDate}`}>
                                    <td
                                      style={{ 
                                        padding: '6px', 
                                        textAlign: 'center', 
                                        borderBottom: index === pitches.length - 1 ? 'none' : '1px solid #e5e7eb', 
                                        borderLeft: '1px solid #e5e7eb', 
                                        cursor: 'pointer' 
                                      }}
                                      onClick={() => navigate(`/allocator/${pitch.id}?date=${header.fullDate}`)}
                                      title={`Click to view allocations for ${displayName} on ${header.fullDate}`}
                                    >
                                      <div
                                        style={{ 
                                          width: '28px', 
                                          height: '28px', 
                                          borderRadius: '50%', 
                                          backgroundColor: getTrafficLightColor(amCapacity), 
                                          margin: '0 auto', 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          justifyContent: 'center', 
                                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', 
                                          transition: 'transform 0.2s ease' 
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                      >
                                        <span style={{ fontSize: '9px', color: 'white', fontWeight: '600' }}>{Math.round(amCapacity)}%</span>
                                      </div>
                                    </td>
                                    <td
                                      style={{ 
                                        padding: '6px', 
                                        textAlign: 'center', 
                                        borderBottom: index === pitches.length - 1 ? 'none' : '1px solid #e5e7eb', 
                                        borderLeft: '1px solid #e5e7eb', 
                                        cursor: 'pointer' 
                                      }}
                                      onClick={() => navigate(`/allocator/${pitch.id}?date=${header.fullDate}`)}
                                      title={`Click to view allocations for ${displayName} on ${header.fullDate}`}
                                    >
                                      <div
                                        style={{ 
                                          width: '28px', 
                                          height: '28px', 
                                          borderRadius: '50%', 
                                          backgroundColor: getTrafficLightColor(pmCapacity), 
                                          margin: '0 auto', 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          justifyContent: 'center', 
                                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', 
                                          transition: 'transform 0.2s ease' 
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                      >
                                        <span style={{ fontSize: '9px', color: 'white', fontWeight: '600' }}>{Math.round(pmCapacity)}%</span>
                                      </div>
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                  
                  {pitches.length === 0 && (
                    <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
                      No pitches configured. Please set up pitches in the satellite configuration.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECTION 2: Monthly Usage Heatmap */}
        <div style={{ marginBottom: '20px' }}>
          <div 
            onClick={() => handleReportingToggle('Monthly Usage Heatmap', showPitchHeatmap, setShowPitchHeatmap, setHeatmapLoaded)}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '16px 20px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!showPitchHeatmap) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>🔥</span>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Monthly Usage Heatmap
              </h2>
              {showPitchHeatmap && heatmapData && (
                <span style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  {getMonthDisplayName(heatmapMonth)}
                </span>
              )}
            </div>
            <span style={{
              fontSize: '20px',
              color: '#6b7280',
              transform: showPitchHeatmap ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}>
              ▼
            </span>
          </div>

          {showPitchHeatmap && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '20px',
              marginTop: '8px',
              animation: 'slideIn 0.3s ease'
            }}>
              {/* Month Navigation */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  <button
                    onClick={navigateToPreviousMonth}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    ← Previous
                  </button>
                  {heatmapMonth !== `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` && (
                    <button
                      onClick={navigateToCurrentMonth}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#3b82f6',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: '500'
                      }}
                    >
                      Current Month
                    </button>
                  )}
                  <button
                    onClick={navigateToNextMonth}
                    disabled={heatmapMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: heatmapMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` ? '#f3f4f6' : 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: heatmapMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      color: heatmapMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` ? '#9ca3af' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: heatmapMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` ? 0.5 : 1
                    }}
                  >
                    Next →
                  </button>
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  {getMonthDisplayName(heatmapMonth)}
                </div>
              </div>

              {/* Legend */}
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Usage Intensity:</span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '16px', height: '16px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>None<br/><span style={{ fontSize: '9px' }}>(0%)</span></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '16px', height: '16px', backgroundColor: '#dcfce7' }} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>Low<br/><span style={{ fontSize: '9px' }}>(1-20%)</span></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '16px', height: '16px', backgroundColor: '#86efac' }} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>Medium<br/><span style={{ fontSize: '9px' }}>(21-40%)</span></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '16px', height: '16px', backgroundColor: '#fbbf24' }} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>High<br/><span style={{ fontSize: '9px' }}>(41-60%)</span></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '16px', height: '16px', backgroundColor: '#fb923c' }} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>Very High<br/><span style={{ fontSize: '9px' }}>(61-80%)</span></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '16px', height: '16px', backgroundColor: '#ef4444' }} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>Maximum<br/><span style={{ fontSize: '9px' }}>(81-100%)</span></span>
                  </div>
                </div>
              </div>

              {/* Heatmaps */}
              {heatmapLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <div style={{
                    display: 'inline-block',
                    animation: 'spin 1s linear infinite',
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e5e7eb',
                    borderTop: '3px solid #3b82f6',
                    borderRadius: '50%',
                    marginBottom: '12px'
                  }}></div>
                  <div>Loading monthly usage data...</div>
                </div>
              ) : heatmapData ? (
                <div>
                  {/* Heatmap Grid - Grouped by facility when "All Locations" selected */}
                  {(() => {
                    // If specific facility selected, just show those pitches
                    if (selectedFacilityId !== 'all' && facilities.length > 0) {
                      const filteredPitches = pitches.filter(pitch => {
                        const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
                        return facilityPitchMap[pitchKey] === selectedFacilityId;
                      });
                      
                      return (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                          gap: '16px'
                        }}>
                          {filteredPitches.map(pitch => {
                            const pitchId = normalizePitchId(pitch.id);
                            const pitchHeatmapData = heatmapData.pitchData[pitchId];
                            return (
                              <PitchHeatmap
                                key={pitchId}
                                pitchId={pitchId}
                                normalizedPitchId={pitchId}
                                data={pitchHeatmapData ? pitchHeatmapData.data : {}}
                                maxValue={pitchHeatmapData ? pitchHeatmapData.maxValue : 0}
                                rawCounts={pitchHeatmapData ? pitchHeatmapData.rawCounts : {}}
                              />
                            );
                          })}
                        </div>
                      );
                    }
                    
                    // "All Locations" selected - group by facility
                    if (facilities.length > 0) {
                      return facilities.map((facility) => {
                        const facilityPitches = pitches.filter(pitch => {
                          const pitchKey = `pitch-${String(pitch.id).replace(/\D/g, '')}`;
                          return facilityPitchMap[pitchKey] === facility.id;
                        });
                        
                        if (facilityPitches.length === 0) return null;
                        
                        return (
                          <div key={facility.id} style={{ marginBottom: '24px' }}>
                            {/* Facility Header */}
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#475569',
                              padding: '10px 12px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '8px',
                              marginBottom: '12px',
                              border: '1px solid #e2e8f0'
                            }}>
                              {facility.name}
                            </div>
                            {/* Facility Pitches */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                              gap: '16px'
                            }}>
                              {facilityPitches.map(pitch => {
                                const pitchId = normalizePitchId(pitch.id);
                                const pitchHeatmapData = heatmapData.pitchData[pitchId];
                                return (
                                  <PitchHeatmap
                                    key={pitchId}
                                    pitchId={pitchId}
                                    normalizedPitchId={pitchId}
                                    data={pitchHeatmapData ? pitchHeatmapData.data : {}}
                                    maxValue={pitchHeatmapData ? pitchHeatmapData.maxValue : 0}
                                    rawCounts={pitchHeatmapData ? pitchHeatmapData.rawCounts : {}}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    }
                    
                    // No facilities - show all pitches in grid
                    return (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px'
                      }}>
                        {pitches.map(pitch => {
                          const pitchId = normalizePitchId(pitch.id);
                          const pitchHeatmapData = heatmapData.pitchData[pitchId];
                          return (
                            <PitchHeatmap
                              key={pitchId}
                              pitchId={pitchId}
                              normalizedPitchId={pitchId}
                              data={pitchHeatmapData ? pitchHeatmapData.data : {}}
                              maxValue={pitchHeatmapData ? pitchHeatmapData.maxValue : 0}
                              rawCounts={pitchHeatmapData ? pitchHeatmapData.rawCounts : {}}
                            />
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Summary Stats */}
                  <div style={{
                    marginTop: '24px',
                    padding: '16px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #bae6fd'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '16px'
                    }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#0369a1', marginBottom: '4px' }}>
                          Total Allocations
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#0c4a6e' }}>
                          {Object.values(heatmapData.pitchData).reduce((total, pitchInfo) => 
                            total + Object.values(pitchInfo.rawCounts || {}).reduce((sum, val) => sum + val, 0), 0
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#0369a1', marginBottom: '4px' }}>
                          Days in Month
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#0c4a6e' }}>
                          {heatmapData.daysInMonth}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#0369a1', marginBottom: '4px' }}>
                          Busiest Section
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#0c4a6e' }}>
                          {(() => {
                            let maxSection = { pitch: '', section: '', count: 0 };
                            Object.entries(heatmapData.pitchData).forEach(([pitchId, pitchInfo]) => {
                              Object.entries(pitchInfo.rawCounts || {}).forEach(([section, count]) => {
                                if (count > maxSection.count) {
                                  maxSection = { 
                                    pitch: getPitchDisplayName(pitchId, pitchNames), 
                                    section, 
                                    count 
                                  };
                                }
                              });
                            });
                            return maxSection.count > 0 ? `${maxSection.pitch}-${maxSection.section}` : 'N/A';
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No data available for this month
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECTION 3: Analytics & Reports */}
        <div style={{ marginBottom: '20px' }}>
          <div 
            onClick={() => handleReportingToggle('Analytics & Reports', showAnalytics, setShowAnalytics, setAnalyticsLoaded)}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '16px 20px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!showAnalytics) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Analytics & Reports
              </h2>
              {showAnalytics && analyticsData && (
                <span style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  {analyticsData.dateRange.startDate} to {analyticsData.dateRange.endDate}
                </span>
              )}
            </div>
            <span style={{
              fontSize: '20px',
              color: '#6b7280',
              transform: showAnalytics ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}>
              ▼
            </span>
          </div>

          {showAnalytics && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '20px',
              marginTop: '8px',
              animation: 'slideIn 0.3s ease'
            }}>
              {/* Date Range Selector & Team Activity Summary */}
              <div style={{ 
                marginBottom: '32px',
                padding: '20px',
                backgroundColor: '#f0f9ff',
                borderRadius: '12px',
                border: '2px solid #0ea5e9'
              }}>
                {/* Date Range Selector */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  marginBottom: '20px',
                  padding: '12px',
                  backgroundColor: '#e0f2fe',
                  borderRadius: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e' }}>Date Range:</span>
                  <input
                    type="date"
                    value={analyticsDateRange.start}
                    onChange={(e) => {
                      setAnalyticsDateRange(prev => ({ ...prev, start: e.target.value }));
                      setAnalyticsLoaded(false);
                    }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #0369a1',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                  <span style={{ color: '#0c4a6e' }}>to</span>
                  <input
                    type="date"
                    value={analyticsDateRange.end}
                    onChange={(e) => {
                      setAnalyticsDateRange(prev => ({ ...prev, end: e.target.value }));
                      setAnalyticsLoaded(false);
                    }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #0369a1',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e', marginLeft: '16px' }}>Team:</span>
                  <select
                    value={selectedTeam}
                    onChange={(e) => {
                      setSelectedTeam(e.target.value);
                      setAnalyticsLoaded(false);
                    }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #0369a1',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: 'white',
                      minWidth: '120px'
                    }}
                  >
                    <option value="all">All Teams</option>
                    {teamsList.map((team, index) => {
                      // Handle both string and object formats
                      const teamName = typeof team === 'string' ? team : team.name || String(team);
                      return (
                        <option key={index} value={teamName}>
                          {teamName}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Team Activity Summary */}
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0c4a6e', marginBottom: '12px' }}>
                  {selectedTeam === 'all' ? 'Team Activity Summary' : `${String(selectedTeam)} Team Activity Summary`}
                </h3>
                {analyticsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <div style={{
                      display: 'inline-block',
                      animation: 'spin 1s linear infinite',
                      width: '40px',
                      height: '40px',
                      border: '3px solid #e5e7eb',
                      borderTop: '3px solid #3b82f6',
                      borderRadius: '50%',
                      marginBottom: '12px'
                    }}></div>
                    <div>Analyzing data...</div>
                  </div>
                ) : analyticsData ? (
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#eff6ff',
                        borderRadius: '8px',
                        border: '1px solid #bfdbfe'
                      }}>
                        <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '4px' }}>
                          Total Sessions
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: '#1e3a8a' }}>
                          {Number(analyticsData.teamActivity.totalSessions || 0)}
                        </div>
                        {selectedTeam !== 'all' && (
                          <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                            {String(selectedTeam)} only
                          </div>
                        )}
                      </div>
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        border: '1px solid #bbf7d0'
                      }}>
                        <div style={{ fontSize: '12px', color: '#166534', marginBottom: '4px' }}>
                          Average Session Length
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: '#14532d' }}>
                          {Number(analyticsData.teamActivity.avgSessionLength || 0)} min
                        </div>
                        {selectedTeam !== 'all' && (
                          <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                            {String(selectedTeam)} only
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Sessions per Pitch */}
                    <div style={{
                      backgroundColor: '#f9fafb',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                        Sessions per Pitch
                      </h4>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '12px'
                      }}>
                        {Object.entries(analyticsData.teamActivity.pitchSessions).map(([pitchId, data]) => {
                          // Use the normalized pitch ID for name lookup
                          const originalPitch = pitches.find(p => normalizePitchId(p.id) === pitchId);
                          const pitchName = originalPitch ? String(getPitchDisplayName(originalPitch.id, pitchNames)) : `Pitch ${String(pitchId).replace(/\D/g, '')}`;
                          const sessionCount = Number(data?.sessions || 0);
                          const totalDuration = Number(data?.totalDuration || 0);
                          
                          return (
                            <div key={pitchId} style={{
                              padding: '12px',
                              backgroundColor: 'white',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '12px', fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                                {pitchName}
                              </div>
                              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>
                                {sessionCount} sessions
                              </div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                {Math.round(totalDuration)} total minutes
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    No analytics data available for the selected date range
                  </div>
                )}
              </div>

              {analyticsData && (
                <>
                  {/* All Teams Analytics - Pitch Peak Time Analysis & Utilization Data */}
                  <div style={{ 
                    marginBottom: '32px',
                    padding: '20px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '12px',
                    border: '2px solid #f59e0b'
                  }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#92400e', marginBottom: '12px' }}>
                      Pitch Peak Time Analysis & Insights
                      <span style={{ fontSize: '12px', fontWeight: '400', color: '#a16207', marginLeft: '8px' }}>
                        (All Teams Data)
                      </span>
                    </h3>
                    <div style={{
                      backgroundColor: '#fffbeb',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #fde68a',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '12px' }}>
                        Most Popular Time Slots
                      </h4>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '8px'
                      }}>
                        {analyticsData.peakTimes.map(({ time, count }, index) => {
                          // Ensure we have valid string values
                          const timeStr = String(time || '');
                          const countNum = Number(count || 0);
                          
                          return (
                            <div key={timeStr} style={{
                              padding: '8px 12px',
                              backgroundColor: index === 0 ? '#f59e0b' : 'white',
                              color: index === 0 ? 'white' : '#92400e',
                              borderRadius: '4px',
                              textAlign: 'center',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              <div>{timeStr}</div>
                              <div style={{ fontSize: '10px', opacity: 0.8 }}>{countNum} bookings</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Utilization Analysis (All Teams Data) */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: '16px',
                      marginBottom: '24px'
                    }}>
                      {/* Underutilized Analysis */}
                      <div style={{
                        backgroundColor: '#f8fafc',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '12px' }}>
                          Underutilized Time Slots
                          <span style={{ fontSize: '11px', fontWeight: '400', color: '#64748b', marginLeft: '6px' }}>
                            (All Teams)
                          </span>
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {analyticsData.underutilizedSlots.slice(0, 3).map(({ time, count }) => {
                            const timeStr = String(time || '');
                            const countNum = Number(count || 0);
                            
                            return (
                              <div key={timeStr} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '6px 8px',
                                backgroundColor: 'white',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                <span style={{ fontWeight: '500' }}>{timeStr}</span>
                                <span style={{ color: '#64748b' }}>{countNum} bookings</span>
                              </div>
                            );
                          })}
                        </div>
                        
                        <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#475569', marginTop: '12px', marginBottom: '8px' }}>
                          Underutilized Sections
                          <span style={{ fontSize: '10px', fontWeight: '400', color: '#64748b', marginLeft: '6px' }}>
                            (All Teams)
                          </span>
                        </h5>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {Object.entries(analyticsData.underutilizedSections).map(([pitchId, sections]) => {
                            // Use the normalized pitch ID for name lookup
                            const originalPitch = pitches.find(p => normalizePitchId(p.id) === pitchId);
                            const pitchName = originalPitch ? String(getPitchDisplayName(originalPitch.id, pitchNames)) : String(pitchId);
                            
                            return (
                              <div key={pitchId}>
                                <div style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                                  {pitchName}
                                </div>
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                  {sections.map(({ section, count }) => {
                                    const sectionStr = String(section || '');
                                    const countNum = Number(count || 0);
                                    
                                    return (
                                      <span key={sectionStr} style={{
                                        padding: '2px 6px',
                                        backgroundColor: 'white',
                                        borderRadius: '3px',
                                        fontSize: '10px',
                                        color: '#475569'
                                      }}>
                                        {sectionStr}: {countNum}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Overutilized Analysis */}
                      <div style={{
                        backgroundColor: '#fafafa',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e5e5'
                      }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#525252', marginBottom: '12px' }}>
                          Heavily Used Sections
                          <span style={{ fontSize: '11px', fontWeight: '400', color: '#737373', marginLeft: '6px' }}>
                            (All Teams)
                          </span>
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {Object.entries(analyticsData.overutilizedSections).map(([pitchId, sections]) => {
                            // Use the normalized pitch ID for name lookup
                            const originalPitch = pitches.find(p => normalizePitchId(p.id) === pitchId);
                            const pitchName = originalPitch ? String(getPitchDisplayName(originalPitch.id, pitchNames)) : String(pitchId);
                            
                            return (
                              <div key={pitchId}>
                                <div style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                                  {pitchName}
                                </div>
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                  {sections.map(({ section, count }) => {
                                    const sectionStr = String(section || '');
                                    const countNum = Number(count || 0);
                                    
                                    return (
                                      <span key={sectionStr} style={{
                                        padding: '4px 8px',
                                        backgroundColor: countNum > 20 ? '#dc2626' : countNum > 10 ? '#f59e0b' : '#6b7280',
                                        color: 'white',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                      }}>
                                        Section {sectionStr}: {countNum} uses
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Key Insights (All Teams Data) */}
                    <div style={{
                      backgroundColor: '#f1f5f9',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1'
                    }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '12px' }}>
                        Key Insights
                        <span style={{ fontSize: '11px', fontWeight: '400', color: '#64748b', marginLeft: '6px' }}>
                          (All Teams Data)
                        </span>
                      </h4>
                      <div style={{ fontSize: '12px', color: '#334155', lineHeight: '1.5' }}>
                        {analyticsData.teamActivity.totalSessions > 0 ? (
                          <div>
                            <p style={{ margin: '0 0 8px 0' }}>
                              • Peak activity occurs at <strong>{String(analyticsData.peakTimes[0]?.time || 'N/A')}</strong> with {Number(analyticsData.peakTimes[0]?.count || 0)} bookings
                            </p>
                            <p style={{ margin: '0 0 8px 0' }}>
                              • Average session length is <strong>{Number(analyticsData.teamActivity.avgSessionLength || 0)} minutes</strong>
                            </p>
                            <p style={{ margin: '0 0 8px 0' }}>
                              • Consider promoting usage during {String(analyticsData.underutilizedSlots[0]?.time || 'N/A')} (only {Number(analyticsData.underutilizedSlots[0]?.count || 0)} bookings)
                            </p>
                            <p style={{ margin: '0' }}>
                              • Total of <strong>{Number(analyticsData.teamActivity.totalSessions || 0)} training sessions</strong> recorded in this period
                            </p>
                          </div>
                        ) : (
                          <p style={{ margin: 0 }}>No session data available for the selected date range.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Monthly Report Generator Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '24px',
          marginTop: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <span style={{ fontSize: '18px' }}>📊</span>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: 0
            }}>
              Monthly Report & Data Export
            </h2>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '16px',
            lineHeight: '1.5'
          }}>
            Export heatmap and capacity data to Excel, or generate an AI-powered facility utilisation report for committee meetings and planning.
          </p>
          <button
            onClick={() => {
              if (!hasReportingAccess) {
                setAttemptedFeature('Monthly Report Generator');
                setShowPremiumModal(true);
                return;
              }
              setShowReportGenerator(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
              padding: '14px 24px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
          >
            <FileText size={18} />
            Generate Monthly Report & Export Data
          </button>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Feedback / Report a Bug
              </h3>
              <button
                onClick={closeFeedbackModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  From (Your Email)
                </label>
                <input
                  type="email"
                  value={feedbackForm.from}
                  onChange={(e) => handleFeedbackInputChange('from', e.target.value)}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Topic
                </label>
                <select
                  value={feedbackForm.topic}
                  onChange={(e) => handleFeedbackInputChange('topic', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select a topic...</option>
                  {feedbackTopics.map((topic, index) => (
                    <option key={index} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>
				
				<div style={{ marginBottom: '16px' }}>
				  <label style={{
				    display: 'block',
				    fontSize: '14px',
				    fontWeight: '500',
				    color: '#374151',
				    marginBottom: '6px'
				  }}>
				    Page URL
				  </label>
				  <input
				    type="text"
				    value={feedbackForm.url}
				    onChange={(e) => handleFeedbackInputChange('url', e.target.value)}
				    placeholder="The page where you encountered this issue"
				    style={{
				      width: '100%',
				      padding: '8px 12px',
				      border: '1px solid #d1d5db',
				      borderRadius: '6px',
				      fontSize: '14px',
				      boxSizing: 'border-box',
				      backgroundColor: '#f9fafb', // Light gray background to show it's auto-filled
				      fontFamily: 'monospace',
				      color: '#6b7280'
				    }}
				    readOnly={false} // Allow editing in case they want to change it
				  />
				  <p style={{
				    fontSize: '12px',
				    color: '#9ca3af',
				    marginTop: '4px',
				    marginBottom: '0'
				  }}>
				    Auto-filled with current page URL
				  </p>
				</div>
				
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Message
                </label>
                <textarea
                  value={feedbackForm.content}
                  onChange={(e) => handleFeedbackInputChange('content', e.target.value)}
                  placeholder="Please describe your feedback, bug report, or suggestion in detail..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={closeFeedbackModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#374151'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitFeedback}
                disabled={isSubmittingFeedback}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isSubmittingFeedback ? '#9ca3af' : '#22c55e',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer',
                  color: 'white',
                  fontWeight: '500',
                  opacity: isSubmittingFeedback ? 0.7 : 1
                }}
              >
                {isSubmittingFeedback ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for responsive design and animations */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .desktop-sidebar {
            display: none !important;
          }
          .mobile-menu-overlay {
            display: block !important;
          }
          .mobile-header {
            display: flex !important;
          }
          .desktop-header {
            display: none !important;
          }
          .mobile-text {
            display: inline !important;
          }
          .desktop-text {
            display: none !important;
          }
        }
        
        @media (min-width: 769px) {
          .desktop-sidebar {
            display: flex !important;
          }
          .mobile-menu-overlay {
            display: none !important;
          }
          .mobile-header {
            display: none !important;
          }
          .desktop-header {
            display: flex !important;
          }
          .mobile-text {
            display: none !important;
          }
          .desktop-text {
            display: inline !important;
          }
        }
      `}</style>
      
      {/* Premium Feature Modal */}
      <PremiumFeatureModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        featureName={attemptedFeature}
        currentTier={subscriptionTier}
        requiredTiers={['standard', 'premium']}
        benefits={
          attemptedFeature === 'Weekly Capacity Overview' ? [
            "View detailed weekly capacity across all your pitches",
            "Track utilization rates and identify available time slots",
            "Optimize pitch usage and maximize revenue",
            "Make data-driven scheduling decisions",
            "Export capacity reports for stakeholders"
          ] :
          attemptedFeature === 'Monthly Usage Heatmap' ? [
            "Visualize pitch usage patterns across the entire month",
            "Identify peak and low-usage periods at a glance",
            "Spot trends and seasonal patterns",
            "Plan maintenance during low-usage times",
            "Optimize scheduling based on usage data"
          ] :
          attemptedFeature === 'Analytics & Reports' ? [
            "Comprehensive analytics on team usage and performance",
            "Track key metrics like hours booked and sessions completed",
            "Compare team activity and identify patterns",
            "Generate professional reports for management",
            "Make evidence-based decisions about resource allocation"
          ] :
          attemptedFeature === 'Allocation Search' ? [
            "Quickly find specific allocations across any date range",
            "Search by team, pitch, date, or session type",
            "Export search results for record-keeping",
            "Audit historical bookings and usage",
            "Resolve scheduling conflicts efficiently"
          ] :
          attemptedFeature === 'Monthly Report Generator' ? [
            "Export capacity and heatmap data to Excel spreadsheets",
            "Generate AI-powered monthly facility utilization reports",
            "Comprehensive team analytics and usage patterns",
            "Professional reports for committee meetings and stakeholders",
            "Identify optimization opportunities with data-driven insights"
          ] : [
            "Access comprehensive reporting features",
            "Make data-driven decisions",
            "Track utilization and performance",
            "Export professional reports",
            "Optimize your facility management"
          ]
        }
        upgradeUrl="/pricing"
      />

      {/* Monthly Report Generator Modal */}
      {showReportGenerator && (
        <MonthlyReportGenerator
          clubId={clubId}
          clubName={clubName}
          pitches={pitches}
          pitchConfigurations={pitchConfigurations}
          teamsList={teamsList}
          onClose={() => setShowReportGenerator(false)}
        />
      )}
    </div>
  );
};

export default CapacityOutlook;
